// =============================================================================
// MODULE AIDES - Simulateur, CEE, MaPrimeRénov
// =============================================================================

import { useState, useEffect } from "react";
import { isInstallateurProfile } from "../../utils/tenantProfile";
import { currentShowsMpr, aidesLabel } from "../../utils/profileLabels";
import {
  Calculator,
  FileText,
  Euro,
  Home,
  Users,
  MapPin,
  Leaf,
  TrendingUp,
  Building2,
  Search,
  Plus,
  Settings,
  ChevronRight,
  Info,
  AlertTriangle,
  CheckCircle,
  Download,
  Upload,
  Trash2,
  Edit,
  Eye,
  ScrollText,
  Variable,
  Save,
  X,
  RefreshCw,
  Accessibility
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import { toast } from "sonner";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "../../components/ui/collapsible";
import DocumentsEngineTemplates from "./DocumentsEngineTemplates";
import DocumentsSourcesManager from "./DocumentsSourcesManager";
import VariablesManager from "./VariablesManager";
import SimulateurMPA from "./SimulateurMPA";
import { useConfigList } from "../../hooks/useConfigList";
// Composants normés charte (§4) — encarts d'état / cartes dual-mode.
import { OhCard, OhStatusNote } from "../../components/ui/oh-charte";
import { enteteTenant } from "../../utils/enteteTenant";
import { nombreFR } from "../../utils/formatNombre";

// Couleurs des catégories MPR (bleu/jaune/violet/rose) dérivées des tokens stat
// dual-mode — jamais de hex ni d'utility Tailwind hardcodée (charte §1/§8).
// La "rose" (revenus supérieurs) mappe la famille red faute de famille pink.
const MPR_COL_TOKEN = {
  bleu: "var(--oh-stat-blue-text)",
  jaune: "var(--oh-stat-yellow-text)",
  violet: "var(--oh-stat-purple-text)",
  rose: "var(--oh-stat-red-text)",
};

const API = process.env.REACT_APP_BACKEND_URL;

// =============================================================================
// COMPOSANT MENTIONS OBLIGATOIRES AIDES
// =============================================================================
// Variables disponibles pour les mentions
const AVAILABLE_VARIABLES = [
  { key: "{{client_nom}}", label: "Nom du client", category: "Client" },
  { key: "{{client_prenom}}", label: "Prénom du client", category: "Client" },
  { key: "{{client_adresse}}", label: "Adresse du client", category: "Client" },
  { key: "{{logement_adresse}}", label: "Adresse du logement", category: "Logement" },
  { key: "{{logement_type}}", label: "Type de logement", category: "Logement" },
  { key: "{{logement_surface}}", label: "Surface du logement", category: "Logement" },
  { key: "{{logement_annee}}", label: "Année de construction", category: "Logement" },
  { key: "{{devis_numero}}", label: "Numéro du devis", category: "Devis" },
  { key: "{{devis_date}}", label: "Date du devis", category: "Devis" },
  { key: "{{devis_montant_ht}}", label: "Montant HT", category: "Devis" },
  { key: "{{devis_montant_ttc}}", label: "Montant TTC", category: "Devis" },
  { key: "{{aide_mpr}}", label: "Montant aide MPR", category: "Aides" },
  { key: "{{aide_cee}}", label: "Montant aide CEE", category: "Aides" },
  { key: "{{reste_charge}}", label: "Reste à charge", category: "Aides" },
  { key: "{{entreprise_nom}}", label: "Nom de l'entreprise", category: "Entreprise" },
  { key: "{{entreprise_siret}}", label: "SIRET", category: "Entreprise" },
  { key: "{{entreprise_rge}}", label: "Numéro RGE", category: "Entreprise" },
  { key: "{{date_signature}}", label: "Date de signature", category: "Signature" },
  { key: "{{date_debut_travaux}}", label: "Date début travaux", category: "Travaux" },
  { key: "{{date_fin_travaux}}", label: "Date fin travaux", category: "Travaux" },
];

// Mentions par défaut pour les aides
const DEFAULT_MENTIONS_AIDES = {
  mpr: {
    title: "Mentions MaPrimeRénov'",
    content: `En application du décret n°2020-26 du 14 janvier 2020 et de ses arrêtés d'application, le client bénéficie d'une aide MaPrimeRénov' de {{aide_mpr}} €.

Cette aide est conditionnée à :
- La réalisation des travaux par une entreprise RGE ({{entreprise_rge}})
- La fourniture des justificatifs requis
- Le respect des critères techniques de performance`
  },
  cee: {
    title: "Mentions CEE (Prime Énergie)",
    content: `Dans le cadre du dispositif des Certificats d'Économies d'Énergie (CEE), le client bénéficie d'une prime de {{aide_cee}} €.

Conformément à l'article L.221-1 du Code de l'énergie, cette prime est versée sous réserve de :
- Signature du devis avant le début des travaux
- Fourniture de l'attestation sur l'honneur
- Transmission du cadre de contribution CEE`
  },
  delai_retractation: {
    title: "Droit de rétractation",
    content: `Conformément aux articles L.221-18 et suivants du Code de la consommation, vous disposez d'un délai de 14 jours à compter de la signature du présent devis pour exercer votre droit de rétractation.

Pour exercer ce droit, vous pouvez utiliser le formulaire de rétractation ci-joint ou adresser une déclaration écrite à {{entreprise_nom}}.`
  },
  conditions_paiement: {
    title: "Conditions de paiement",
    content: `Le paiement s'effectue selon les modalités suivantes :
- Acompte à la commande : 30% du montant TTC
- Solde à la réception des travaux

Les aides MaPrimeRénov' ({{aide_mpr}} €) et CEE ({{aide_cee}} €) seront déduites du montant à votre charge.
Reste à charge client : {{reste_charge}} €`
  },
  garanties: {
    title: "Garanties",
    content: `Les travaux sont couverts par :
- Garantie décennale n°[NUMERO_GARANTIE]
- Garantie de parfait achèvement : 1 an
- Garantie constructeur sur les équipements

L'entreprise {{entreprise_nom}} est assurée en responsabilité civile professionnelle.`
  }
};


// =============================================================================
// COMPOSANT LISTE STATUTS OMV (éditable per-tenant — metier_config_listes)
// =============================================================================
// Liste "statut dossier OMV" : champ libre (PAS de workflow/transitions), éditée
// par Solène (ajout/retrait/réordonnancement). Réutilise les endpoints existants
// GET/PUT /api/metier/config-listes/statuts_omv (tenant depuis contexte). Gating
// omv_france côté appelant. Badges monochromes (couleurs = évolution future).
// Éditeur générique de liste config per-tenant (id=label, texte libre). Réutilisé
// pour plusieurs listes OMV (statuts dossier, secteurs d'activité…) via `listType`.
function OmvConfigListEditor({ tenantId, listType, title, description, placeholder, savedMsg = "Liste enregistrée", dupMsg = "Valeur déjà présente", defaultOpen = false, slugifyId = false }) {
  const API = process.env.REACT_APP_BACKEND_URL;
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [newLabel, setNewLabel] = useState("");
  // Section repliable : cet écran agrège plusieurs listes → on replie celles
  // qu'on ne modifie pas (sinon l'écran est trop long). 1ʳᵉ ouverte par défaut.
  const [open, setOpen] = useState(defaultOpen);

  const buildHeaders = () => {
    const tok = localStorage.getItem("session_token");
    return {
      "Content-Type": "application/json",
      ...enteteTenant(tenantId),
      ...(tok ? { Authorization: `Bearer ${tok}` } : {}),
    };
  };

  const load = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/metier/config-listes/${listType}`, {
        headers: buildHeaders(), credentials: "include",
      });
      if (res.ok) {
        const data = await res.json();
        setItems((data.items || []).map(it => ({ id: it.id ?? it.label, label: it.label ?? it.id })));
      }
    } catch { /* noop */ }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tenantId, listType]);

  const persist = async (next) => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/metier/config-listes/${listType}`, {
        method: "PUT", headers: buildHeaders(), credentials: "include",
        body: JSON.stringify({ items: next }),
      });
      if (res.ok) { setItems(next); toast.success(savedMsg); }
      else { const e = await res.json().catch(() => ({})); toast.error(e.detail || "Erreur enregistrement"); }
    } catch { toast.error("Erreur réseau"); }
    finally { setSaving(false); }
  };

  // Code technique stable (ex. type_document) : on dérive un slug du libellé
  // (minuscule, sans accent, _). Les listes libres (statuts/secteurs) gardent
  // id = label.
  const toSlug = (s) => s.normalize("NFD").replace(/[̀-ͯ]/g, "")
    .toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "");

  const addItem = () => {
    const lab = newLabel.trim();
    if (!lab) return;
    if (items.some(it => (it.label || "").toLowerCase() === lab.toLowerCase())) {
      toast.error(dupMsg); return;
    }
    const id = slugifyId ? toSlug(lab) : lab;
    if (slugifyId && items.some(it => it.id === id)) { toast.error(dupMsg); return; }
    persist([...items, { id, label: lab }]);
    setNewLabel("");
  };
  const removeItem = (idx) => persist(items.filter((_, i) => i !== idx));
  const move = (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= items.length) return;
    const next = items.slice();
    [next[idx], next[j]] = [next[j], next[idx]];
    persist(next);
  };

  return (
    <Card className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]">
      <Collapsible open={open} onOpenChange={setOpen}>
        <CollapsibleTrigger asChild>
          <CardHeader
            className="cursor-pointer select-none hover:bg-[var(--oh-bg-elevated)] rounded-t-lg"
            data-testid={`omv-list-section-${listType}`}
            aria-expanded={open}
          >
            <div className="flex items-center justify-between gap-2">
              <div className="min-w-0">
                <CardTitle className="text-[var(--oh-text)] text-base flex items-center gap-2">
                  <ChevronRight className={`w-4 h-4 shrink-0 transition-transform ${open ? "rotate-90" : ""}`} />
                  {title}
                  <Badge className="bg-[var(--oh-bg-elevated)] text-[var(--oh-text-secondary)] text-xs">{items.length}</Badge>
                </CardTitle>
                <p className="text-sm text-[var(--oh-text-secondary)] mt-1 ml-6">{description}</p>
              </div>
            </div>
          </CardHeader>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <CardContent className="space-y-3">
            <div className="flex gap-2">
              <Input
                value={newLabel}
                onChange={(e) => setNewLabel(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") addItem(); }}
                placeholder={placeholder}
                className="bg-[var(--oh-bg-surface)] border-[var(--oh-border)] text-[var(--oh-text)]"
                data-testid={`omv-list-new-input-${listType}`}
              />
              <Button onClick={addItem} disabled={saving || !newLabel.trim()} data-testid={`omv-list-add-btn-${listType}`}>
                <Plus className="w-4 h-4 mr-1" /> Ajouter
              </Button>
            </div>
            {loading ? (
              <p className="text-sm text-[var(--oh-text-muted)]">Chargement…</p>
            ) : items.length === 0 ? (
              <p className="text-sm text-[var(--oh-text-muted)]">Aucune valeur. Ajoutez-en ci-dessus.</p>
            ) : (
              <ul className="space-y-1" data-testid={`omv-list-${listType}`}>
                {items.map((it, idx) => (
                  <li key={it.id ?? idx}
                    className="flex items-center justify-between gap-2 px-3 py-2 rounded bg-[var(--oh-bg-surface)] border border-[var(--oh-border)]">
                    <span className="text-sm text-[var(--oh-text)]">
                      <span className="text-[var(--oh-text-muted)] mr-2">{idx + 1}.</span>{it.label}
                    </span>
                    <span className="flex items-center gap-1">
                      <Button variant="ghost" size="sm" className="h-7 px-2 text-[var(--oh-text-secondary)]"
                        disabled={saving || idx === 0} onClick={() => move(idx, -1)} aria-label="Monter">↑</Button>
                      <Button variant="ghost" size="sm" className="h-7 px-2 text-[var(--oh-text-secondary)]"
                        disabled={saving || idx === items.length - 1} onClick={() => move(idx, 1)} aria-label="Descendre">↓</Button>
                      <Button variant="ghost" size="sm" className="h-7 px-2 text-[var(--oh-error-text)]"
                        disabled={saving} onClick={() => removeItem(idx)} aria-label="Supprimer">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  );
}

// =============================================================================
// COMPOSANT PLAFONDS CUMUL AIDES
// =============================================================================
function PlafondsCumulManager({ tenantId }) {
  const [plafonds, setPlafonds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingPlafond, setEditingPlafond] = useState(null);
  const [formData, setFormData] = useState({
    code: "",
    fiche_cee: "",
    description: "",
    plafond_total: "",
    priorite_deduction: "cee",
    date_debut: new Date().toISOString().split('T')[0],
    date_fin: "",
    référence: ""
  });

  const getAuthHeaders = () => {
    const token = localStorage.getItem('session_token');
    return {
      ...enteteTenant(tenantId),
      "Content-Type": "application/json",
      ...(token && { "Authorization": `Bearer ${token}` })
    };
  };

  // Charger les plafonds
  const loadPlafonds = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/metier/aides/plafonds?actif_only=false`, {
        headers: getAuthHeaders(),
        credentials: 'include'
      });
      if (res.ok) {
        const data = await res.json();
        setPlafonds(data);
      }
    } catch (error) {
      console.error("Erreur chargement plafonds:", error);
      toast.error("Erreur de chargement des plafonds");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPlafonds();
  }, [tenantId]);

  // Sauvegarder un plafond
  const savePlafond = async () => {
    if (!formData.code || !formData.fiche_cee || !formData.plafond_total) {
      toast.error("Veuillez remplir les champs obligatoires");
      return;
    }

    try {
      const payload = {
        ...formData,
        plafond_total: parseFloat(formData.plafond_total),
        date_fin: formData.date_fin || null
      };

      let res;
      if (editingPlafond) {
        res = await fetch(`${API}/api/metier/aides/plafonds/${editingPlafond.id}`, {
          method: "PUT",
          headers: getAuthHeaders(),
          credentials: 'include',
          body: JSON.stringify(payload)
        });
      } else {
        res = await fetch(`${API}/api/metier/aides/plafonds`, {
          method: "POST",
          headers: getAuthHeaders(),
          credentials: 'include',
          body: JSON.stringify(payload)
        });
      }

      if (res.ok) {
        toast.success(editingPlafond ? "Plafond mis à jour" : "Plafond créé");
        setShowAddModal(false);
        setEditingPlafond(null);
        resetForm();
        loadPlafonds();
      } else {
        const error = await res.json();
        toast.error(error.detail || "Erreur lors de la sauvegarde");
      }
    } catch (error) {
      console.error("Erreur sauvegarde plafond:", error);
      toast.error("Erreur lors de la sauvegarde");
    }
  };

  // Supprimer (désactiver) un plafond
  const deletePlafond = async (plafond) => {
    if (!confirm(`Désactiver le plafond "${plafond.code}" ?`)) return;

    try {
      const res = await fetch(`${API}/api/metier/aides/plafonds/${plafond.id}`, {
        method: "DELETE",
        headers: getAuthHeaders(),
        credentials: 'include'
      });

      if (res.ok) {
        toast.success("Plafond désactivé");
        loadPlafonds();
      }
    } catch (error) {
      console.error("Erreur suppression plafond:", error);
      toast.error("Erreur lors de la suppression");
    }
  };

  // Réactiver un plafond
  const reactivatePlafond = async (plafond) => {
    try {
      const res = await fetch(`${API}/api/metier/aides/plafonds/${plafond.id}`, {
        method: "PUT",
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify({ actif: true })
      });

      if (res.ok) {
        toast.success("Plafond réactivé");
        loadPlafonds();
      }
    } catch (error) {
      console.error("Erreur réactivation plafond:", error);
    }
  };

  const resetForm = () => {
    setFormData({
      code: "",
      fiche_cee: "",
      description: "",
      plafond_total: "",
      priorite_deduction: "cee",
      date_debut: new Date().toISOString().split('T')[0],
      date_fin: "",
      référence: ""
    });
  };

  const openEditModal = (plafond) => {
    setEditingPlafond(plafond);
    setFormData({
      code: plafond.code,
      fiche_cee: plafond.fiche_cee,
      description: plafond.description,
      plafond_total: plafond.plafond_total.toString(),
      priorite_deduction: plafond.priorite_deduction || "cee",
      date_debut: plafond.date_debut,
      date_fin: plafond.date_fin || "",
      référence: plafond.référence || ""
    });
    setShowAddModal(true);
  };

  // Fiches CEE disponibles
  const FICHES_CEE = [
    { code: "BAR-TH-171", label: "PAC Air/Eau" },
    { code: "BAR-TH-148", label: "Chauffe-eau thermodynamique" },
    { code: "BAR-TH-129", label: "PAC Air/Air" },
    { code: "BAR-TH-159", label: "PAC hybride" },
    { code: "BAR-TH-174", label: "Rénovation d'ampleur" }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-medium text-[var(--oh-text)] flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-[var(--oh-accent-text)]" />
            Plafonds de cumul des aides
          </h3>
          <p className="text-sm text-[var(--oh-text-muted)] mt-1">
            Configurez les plafonds maximum pour le cumul MPR + CEE par type d'opération
          </p>
        </div>
        <Button
          onClick={() => { resetForm(); setEditingPlafond(null); setShowAddModal(true); }}
          className="bg-[var(--oh-accent-solid)] hover:bg-[var(--oh-accent-solid)]"
        >
          <Plus className="w-4 h-4 mr-2" />
          Nouveau plafond
        </Button>
      </div>

      {/* Info — encart d'état normé (charte §4). C'est une note informative,
          pas une erreur → variant="info" (dual-mode). */}
      <OhStatusNote variant="info" title="Règle de plafonnement">
        <p>Lorsque le cumul MPR + CEE dépasse le plafond configuré, l'aide prioritaire (généralement CEE) est conservée intégralement et l'autre aide est ajustée.</p>
        <p className="mt-1" style={{ opacity: 0.85 }}>Exemple PAC : si CEE = 9000 € et MPR = 5000 € avec plafond 10800 € → MPR ajusté à 1800 €</p>
      </OhStatusNote>

      {/* Liste des plafonds */}
      <Card className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center text-[var(--oh-text-muted)]">Chargement...</div>
          ) : plafonds.length === 0 ? (
            <div className="p-8 text-center text-[var(--oh-text-muted)]">
              Aucun plafond configuré
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-[var(--oh-border)]">
                  <TableHead className="text-[var(--oh-text-muted)]">Code</TableHead>
                  <TableHead className="text-[var(--oh-text-muted)]">Fiche CEE</TableHead>
                  <TableHead className="text-[var(--oh-text-muted)]">Description</TableHead>
                  <TableHead className="text-[var(--oh-text-muted)] text-right">Plafond</TableHead>
                  <TableHead className="text-[var(--oh-text-muted)]">Priorité</TableHead>
                  <TableHead className="text-[var(--oh-text-muted)]">Validité</TableHead>
                  <TableHead className="text-[var(--oh-text-muted)]">Statut</TableHead>
                  <TableHead className="text-[var(--oh-text-muted)] text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {plafonds.map((plafond) => (
                  <TableRow key={plafond.id} className="border-[var(--oh-border)]">
                    <TableCell className="font-mono text-sm text-[var(--oh-text)]">
                      {plafond.code}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="border-[var(--oh-accent)] text-[var(--oh-accent-text)]">
                        {plafond.fiche_cee}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-[var(--oh-text-muted)] max-w-[200px] truncate">
                      {plafond.description}
                    </TableCell>
                    <TableCell className="text-right font-medium text-[var(--oh-text)]">
                      {plafond.plafond_total.toLocaleString('fr-FR')} €
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className={
                        plafond.priorite_deduction === "cee" 
                          ? "border-[var(--oh-success-border)] text-[var(--oh-success-text)]"
                          : "border-[var(--oh-accent)] text-[var(--oh-accent-text)]"
                      }>
                        {plafond.priorite_deduction === "cee" ? "CEE prioritaire" : "MPR prioritaire"}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-[var(--oh-text-muted)] text-sm">
                      {plafond.date_debut}
                      {plafond.date_fin && ` → ${plafond.date_fin}`}
                    </TableCell>
                    <TableCell>
                      {plafond.actif ? (
                        <Badge className="bg-[var(--oh-success-bg)] text-[var(--oh-success-text)]">Actif</Badge>
                      ) : (
                        <Badge className="bg-[var(--oh-bg-elevated)]/20 text-[var(--oh-text-muted)]">Inactif</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEditModal(plafond)}
                          className="text-[var(--oh-text-muted)] hover:text-[var(--oh-text)]"
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        {plafond.actif ? (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => deletePlafond(plafond)}
                            className="text-[var(--oh-error-text)] hover:text-[var(--oh-error-text)]"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        ) : (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => reactivatePlafond(plafond)}
                            className="text-[var(--oh-success-text)] hover:text-[var(--oh-success-text)]"
                          >
                            <RefreshCw className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Modal Création/Edition */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent className="bg-[var(--oh-bg-surface)] border-[var(--oh-border)] max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-[var(--oh-text)]">
              {editingPlafond ? "Modifier le plafond" : "Nouveau plafond de cumul"}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-[var(--oh-text-muted)]">Code *</Label>
                <Input
                  value={formData.code}
                  onChange={(e) => setFormData({...formData, code: e.target.value.toUpperCase()})}
                  placeholder="PLAFOND_PAC_AIR_EAU"
                  className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)] text-[var(--oh-text)]"
                  disabled={!!editingPlafond}
                />
              </div>
              <div className="space-y-2">
                <Label className="text-[var(--oh-text-muted)]">Fiche CEE *</Label>
                <Select
                  value={formData.fiche_cee}
                  onValueChange={(v) => setFormData({...formData, fiche_cee: v})}
                >
                  <SelectTrigger className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)] text-[var(--oh-text)]">
                    <SelectValue placeholder="Sélectionner..." />
                  </SelectTrigger>
                  <SelectContent className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]">
                    {FICHES_CEE.map((f) => (
                      <SelectItem key={f.code} value={f.code} className="text-[var(--oh-text)]">
                        {f.code} - {f.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-[var(--oh-text-muted)]">Description *</Label>
              <Input
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                placeholder="Plafond cumul aides PAC Air/Eau"
                className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)] text-[var(--oh-text)]"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-[var(--oh-text-muted)]">Plafond total (€) *</Label>
                <Input
                  type="number"
                  value={formData.plafond_total}
                  onChange={(e) => setFormData({...formData, plafond_total: e.target.value})}
                  placeholder="10800"
                  className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)] text-[var(--oh-text)]"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-[var(--oh-text-muted)]">Priorité déduction</Label>
                <Select
                  value={formData.priorite_deduction}
                  onValueChange={(v) => setFormData({...formData, priorite_deduction: v})}
                >
                  <SelectTrigger className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)] text-[var(--oh-text)]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]">
                    <SelectItem value="cee" className="text-[var(--oh-text)]">CEE prioritaire</SelectItem>
                    <SelectItem value="mpr" className="text-[var(--oh-text)]">MPR prioritaire</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-[var(--oh-text-muted)]">Date début *</Label>
                <Input
                  type="date"
                  value={formData.date_debut}
                  onChange={(e) => setFormData({...formData, date_debut: e.target.value})}
                  className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)] text-[var(--oh-text)]"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-[var(--oh-text-muted)]">Date fin (optionnel)</Label>
                <Input
                  type="date"
                  value={formData.date_fin}
                  onChange={(e) => setFormData({...formData, date_fin: e.target.value})}
                  className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)] text-[var(--oh-text)]"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-[var(--oh-text-muted)]">Référence réglementaire</Label>
              <Input
                value={formData.référence}
                onChange={(e) => setFormData({...formData, référence: e.target.value})}
                placeholder="Décret MPR 2024"
                className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)] text-[var(--oh-text)]"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddModal(false)} className="border-[var(--oh-border)]">
              Annuler
            </Button>
            <Button onClick={savePlafond} className="bg-[var(--oh-accent-solid)] hover:bg-[var(--oh-accent-solid)]">
              <Save className="w-4 h-4 mr-2" />
              {editingPlafond ? "Mettre à jour" : "Créer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


function MentionsObligatoiresAides({ tenantId }) {
  // Gating MPR par RÉGIME (mpr||anah) : profil CEE-only (OMV) → pas de slot/variable MPR.
  const showMpr = currentShowsMpr();
  const [mentions, setMentions] = useState({});
  const [editingKey, setEditingKey] = useState(null);
  const [editContent, setEditContent] = useState("");
  const [editTitle, setEditTitle] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [openVariables, setOpenVariables] = useState(false);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('session_token');
    return {
      ...enteteTenant(tenantId),
      "Content-Type": "application/json",
      ...(token && { "Authorization": `Bearer ${token}` })
    };
  };

  // Charger les mentions du tenant
  useEffect(() => {
    loadMentions();
  }, [tenantId]);

  // Slots de mentions standard (titres) — le CONTENU vient du tenant (config),
  // VIDE par défaut (jamais le global JKC). Solène remplit ses propres mentions.
  const MENTION_SLOTS = {
    mpr: "Mentions MaPrimeRénov'",
    cee: "Mentions CEE (Prime Énergie)",
    delai_retractation: "Droit de rétractation",
    conditions_paiement: "Conditions de paiement",
    garanties: "Garanties",
  };

  const loadMentions = async () => {
    setLoading(true);
    try {
      // SOURCE per-tenant = tenant.config (lot ISOLATION-C, même clé que la
      // génération devis/contrat). VIDE si non renseigné → plus de JKC global.
      const res = await fetch(`${API}/api/platform/tenants/${tenantId}/mentions`, {
        headers: getAuthHeaders()
      });
      let saved = {};
      if (res.ok) {
        const data = await res.json();
        saved = data.mentions || {};
      }
      const merged = {};
      Object.entries(MENTION_SLOTS).forEach(([k, title]) => {
        merged[k] = {
          title,
          content: (saved[k] && saved[k].content) || "",
          ...(saved[k] && saved[k].delegataire ? { delegataire: saved[k].delegataire } : {}),
        };
      });
      setMentions(merged);
    } catch (error) {
      console.error("Erreur chargement mentions:", error);
      const empty = {};
      Object.entries(MENTION_SLOTS).forEach(([k, title]) => { empty[k] = { title, content: "" }; });
      setMentions(empty);
    } finally {
      setLoading(false);
    }
  };

  // Persiste l'objet mentions fourni. On PUT la valeur PASSÉE (pas l'état `mentions`
  // qui peut ne pas être encore à jour) → évite le piège de closure obsolète.
  const persistMentions = async (mentionsToSave) => {
    setSaving(true);
    try {
      // ÉCRITURE per-tenant (RBAC admin du tenant courant ; Solène → omv_france only)
      const res = await fetch(`${API}/api/platform/tenants/${tenantId}/mentions`, {
        method: "PUT",
        headers: getAuthHeaders(),
        body: JSON.stringify({ mentions: mentionsToSave })
      });

      if (res.ok) {
        toast.success("Mentions enregistrées");
      } else {
        toast.error("Erreur lors de l'enregistrement");
      }
    } catch (error) {
      console.error("Erreur sauvegarde mentions:", error);
      toast.error("Erreur de communication");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveMentions = () => persistMentions(mentions);

  const startEditing = (key) => {
    setEditingKey(key);
    setEditContent(mentions[key]?.content || "");
    setEditTitle(mentions[key]?.title || "");
  };

  const cancelEditing = () => {
    setEditingKey(null);
    setEditContent("");
    setEditTitle("");
  };

  const saveEditing = () => {
    // Construire l'objet mis à jour AVANT le setState pour pouvoir le persister
    // immédiatement (Option A : auto-save par mention — fin du piège "édité mais
    // jamais enregistré" si l'utilisateur quitte sans 'Enregistrer tout').
    const updated = {
      ...mentions,
      [editingKey]: { title: editTitle, content: editContent }
    };
    setMentions(updated);
    setEditingKey(null);
    setEditContent("");
    setEditTitle("");
    persistMentions(updated);  // PUT immédiat de la mention éditée
  };

  const insertVariable = (variable) => {
    if (editingKey) {
      setEditContent(prev => prev + " " + variable);
    }
  };

  const copyVariable = (variable) => {
    navigator.clipboard.writeText(variable);
    toast.success("Variable copiée");
  };

  const addNewMention = () => {
    const newKey = `custom_${Date.now()}`;
    setMentions(prev => ({
      ...prev,
      [newKey]: {
        title: "Nouvelle mention",
        content: ""
      }
    }));
    startEditing(newKey);
  };

  const deleteMention = (key) => {
    if (["mpr", "cee"].includes(key)) {
      toast.error("Cette mention est protégée et ne peut pas être supprimée");
      return;
    }
    const updated = { ...mentions };
    delete updated[key];
    setMentions(updated);
    toast.info("Mention supprimée (n'oubliez pas de sauvegarder)");
  };

  // #3 : masquer la variable {{aide_mpr}} pour un profil CEE-only (pas de MPR).
  const visibleVariables = AVAILABLE_VARIABLES.filter(
    (v) => showMpr || v.key !== "{{aide_mpr}}"
  );

  // Grouper les variables par catégorie
  const variablesByCategory = visibleVariables.reduce((acc, v) => {
    if (!acc[v.category]) acc[v.category] = [];
    acc[v.category].push(v);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Calculator className="w-6 h-6 animate-spin text-[var(--oh-accent-text)]" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]">
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-[var(--oh-text)] font-medium flex items-center gap-2">
                <ScrollText className="w-5 h-5 text-[var(--oh-accent-text)]" />
                Mentions Obligatoires Aides
              </h3>
              <p className="text-[var(--oh-text-muted)] text-sm mt-1">
                Textes légaux pour les devis avec {aidesLabel('aides_mentions_header', showMpr)}. Ces mentions seront automatiquement ajoutées aux documents.
              </p>
            </div>
            <Button
              onClick={handleSaveMentions}
              disabled={saving}
              className="bg-[var(--oh-accent-solid)] hover:bg-[var(--oh-accent-solid)]"
            >
              {saving ? (
                <Calculator className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              Enregistrer tout
            </Button>
          </div>

          {/* Variables disponibles */}
          <Collapsible open={openVariables} onOpenChange={setOpenVariables} className="mt-4">
            <CollapsibleTrigger className="flex items-center gap-2 text-[var(--oh-text-muted)] hover:text-[var(--oh-text)] py-2 border-t border-[var(--oh-border)] w-full">
              <ChevronRight className={`w-4 h-4 transition-transform ${openVariables ? 'rotate-90' : ''}`} />
              <Variable className="w-4 h-4" />
              <span className="text-sm font-medium">Variables disponibles ({visibleVariables.length})</span>
            </CollapsibleTrigger>
            <CollapsibleContent className="pt-3">
              <div className="bg-[var(--oh-bg-surface)] rounded-lg p-4">
                <p className="text-[var(--oh-text-muted)] text-xs mb-3 flex items-center gap-1">
                  <Info className="w-3 h-3" />
                  Cliquez pour copier ou insérez pendant l'édition
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {Object.entries(variablesByCategory).map(([category, vars]) => (
                    <div key={category}>
                      <h5 className="text-[var(--oh-text-muted)] text-xs font-medium mb-2">{category}</h5>
                      <div className="space-y-1">
                        {vars.map(v => (
                          <div 
                            key={v.key}
                            className="flex items-center justify-between text-xs p-1.5 rounded bg-[var(--oh-bg-elevated)] hover:bg-[var(--oh-bg-elevated)] cursor-pointer"
                            onClick={() => editingKey ? insertVariable(v.key) : copyVariable(v.key)}
                          >
                            <span className="text-[var(--oh-text-muted)]">{v.label}</span>
                            <code className="text-[var(--oh-accent-text)] text-[10px]">{v.key}</code>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </CollapsibleContent>
          </Collapsible>
        </CardContent>
      </Card>

      {/* Liste des mentions — #4 : slot "mpr" masqué pour un profil CEE-only. */}
      <div className="grid gap-4">
        {Object.entries(mentions).filter(([key]) => showMpr || key !== "mpr").map(([key, mention]) => (
          <Card key={key} className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base text-[var(--oh-text)] flex items-center gap-2">
                  {["mpr", "cee"].includes(key) && (
                    <Badge className={key === "mpr" ? "bg-[var(--oh-accent-bg)] border border-[var(--oh-accent)]" : "bg-[var(--oh-accent-solid)]"}>
                      {key.toUpperCase()}
                    </Badge>
                  )}
                  {mention.title}
                </CardTitle>
                <div className="flex gap-2">
                  {editingKey === key ? (
                    <>
                      <Button size="sm" variant="outline" onClick={cancelEditing}>
                        <X className="w-4 h-4" />
                      </Button>
                      <Button size="sm" onClick={saveEditing} className="bg-[var(--oh-accent-solid)] hover:bg-[var(--oh-accent-solid)]">
                        <Save className="w-4 h-4 mr-1" />
                        Enregistrer
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => startEditing(key)}
                        className="text-[var(--oh-accent-text)] border-[var(--oh-accent)]"
                      >
                        <Edit className="w-4 h-4 mr-1" />
                        Modifier
                      </Button>
                      {!["mpr", "cee"].includes(key) && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => deleteMention(key)}
                          className="text-[var(--oh-error-text)] border-[var(--oh-error-border)]"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {editingKey === key ? (
                <div className="space-y-3">
                  <div className="space-y-1">
                    <Label className="text-[var(--oh-text-muted)] text-xs">Titre</Label>
                    <Input
                      value={editTitle}
                      onChange={(e) => setEditTitle(e.target.value)}
                      className="bg-[var(--oh-bg-surface)] border-[var(--oh-border)] text-[var(--oh-text)]"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-[var(--oh-text-muted)] text-xs">Contenu</Label>
                    <Textarea
                      value={editContent}
                      onChange={(e) => setEditContent(e.target.value)}
                      rows={8}
                      className="bg-[var(--oh-bg-surface)] border-[var(--oh-border)] text-[var(--oh-text)] font-mono text-sm"
                    />
                  </div>
                  <p className="text-[var(--oh-text-secondary)] text-xs">
                    Cliquez sur les variables ci-dessus pour les insérer
                  </p>
                </div>
              ) : (
                <div className="bg-[var(--oh-bg-surface)] rounded-lg p-4">
                  <pre className="text-[var(--oh-text-muted)] text-sm whitespace-pre-wrap font-sans">
                    {mention.content}
                  </pre>
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Ajouter une mention */}
      <Button
        variant="outline"
        className="w-full border-dashed border-[var(--oh-border)] text-[var(--oh-text-muted)] hover:text-[var(--oh-text)]"
        onClick={addNewMention}
      >
        <Plus className="w-4 h-4 mr-2" />
        Ajouter une mention personnalisée
      </Button>
    </div>
  );
}

// =============================================================================
// BARÈMES MPR 2025 (à jour)
// =============================================================================
const BAREMES_MPR_DEFAULT = {
  idf: {
    1: { bleu: 24031, jaune: 29253, violet: 40851, rose: Infinity },
    2: { bleu: 35270, jaune: 42933, violet: 60051, rose: Infinity },
    3: { bleu: 42357, jaune: 51564, violet: 72056, rose: Infinity },
    4: { bleu: 49455, jaune: 60227, violet: 84313, rose: Infinity },
    5: { bleu: 56571, jaune: 68890, violet: 96570, rose: Infinity },
    parPersonne: { bleu: 7116, jaune: 8663, violet: 12257, rose: 0 }
  },
  province: {
    1: { bleu: 17363, jaune: 22259, violet: 31185, rose: Infinity },
    2: { bleu: 25393, jaune: 32555, violet: 45573, rose: Infinity },
    3: { bleu: 30540, jaune: 39153, violet: 54930, rose: Infinity },
    4: { bleu: 35676, jaune: 45751, violet: 64287, rose: Infinity },
    5: { bleu: 40835, jaune: 52349, violet: 73644, rose: Infinity },
    parPersonne: { bleu: 5151, jaune: 6598, violet: 9357, rose: 0 }
  }
};

// Couleurs MPR — dérivées des tokens stat (dual-mode), consommées via `style`
// (jamais de classe Tailwind hardcodée). `token` = teinte de la catégorie.
const COULEURS_MPR = {
  bleu: { label: "Très modestes", token: MPR_COL_TOKEN.bleu },
  jaune: { label: "Modestes", token: MPR_COL_TOKEN.jaune },
  violet: { label: "Intermédiaires", token: MPR_COL_TOKEN.violet },
  rose: { label: "Supérieurs", token: MPR_COL_TOKEN.rose },
};

// Fiches CEE courantes (ADEME)
const FICHES_CEE_ADEME = [
  { code: "BAR-TH-171", nom: "Pompe à chaleur air/eau", categorie: "Chauffage", kwh_cumac: 84800 },
  { code: "BAR-TH-148", nom: "Chauffe-eau thermodynamique", categorie: "ECS", kwh_cumac: 31500 },
  { code: "BAR-TH-129", nom: "Pompe à chaleur air/air", categorie: "Chauffage", kwh_cumac: 45200 },
  { code: "BAR-TH-104", nom: "Pompe à chaleur haute température", categorie: "Chauffage", kwh_cumac: 95000 },
  { code: "BAR-TH-106", nom: "Chaudière à condensation", categorie: "Chauffage", kwh_cumac: 28600 },
  { code: "BAR-TH-127", nom: "VMC simple flux", categorie: "Ventilation", kwh_cumac: 12500 },
  { code: "BAR-TH-125", nom: "VMC double flux", categorie: "Ventilation", kwh_cumac: 39600 },
  { code: "BAR-EN-101", nom: "Isolation des combles", categorie: "Isolation", kwh_cumac: 1700 },
  { code: "BAR-EN-102", nom: "Isolation des murs", categorie: "Isolation", kwh_cumac: 2500 },
  { code: "BAR-EN-103", nom: "Isolation plancher bas", categorie: "Isolation", kwh_cumac: 1500 }
];

// =============================================================================
// COMPOSANT PRINCIPAL
// =============================================================================
export default function AidesModule({ tenantId, onNavigate }) {
  // OMV (installateur CEE tertiaire) : masque les concepts résidentiels
  // (MaPrimeRénov', Plafonds Cumul) et expose la liste de statuts OMV éditable.
  const isOmv = isInstallateurProfile();
  // Gating MPR par RÉGIME (mpr||anah), jamais par slug : OMV (cee-only) masque le MPR ;
  // TEF (anah) et Climat (mpr) le gardent. Cf. profileLabels.currentShowsMpr.
  const showMpr = currentShowsMpr();
  const { items: logementTypeOptions } = useConfigList("types_logement");
  const { items: occupationOptions } = useConfigList("statuts_occupation");
  const [activeTab, setActiveTab] = useState("simulateur");
  const [loading, setLoading] = useState(false);
  
  // États Simulateur
  const [simulationData, setSimulationData] = useState({
    // Logement
    type_logement: "maison",
    surface: "",
    annee_construction: "",
    dpe_actuel: "",
    region: "province",
    code_postal: "",
    // Ménage
    nb_personnes: 1,
    rfr: "",
    statut: "proprietaire_occupant",
    // Travaux
    operation: "",
    cout_travaux: "",
    artisan_rge: true
  });
  const [simulationResult, setSimulationResult] = useState(null);

  // États CEE
  const [delegataires, setDelegataires] = useState([]);
  const [deals, setDeals] = useState([]);
  const [fichesCEE, setFichesCEE] = useState([]);
  const [searchDelegataire, setSearchDelegataire] = useState("");
  const [searchFiche, setSearchFiche] = useState("");
  const [selectedDelegataire, setSelectedDelegataire] = useState(null);
  const [selectedFiche, setSelectedFiche] = useState(null);
  const [showDelegataireDialog, setShowDelegataireDialog] = useState(false);
  const [showDealDialog, setShowDealDialog] = useState(false);
  // Deal B2B (profil installateur) — variante DISTINCTE du deal B2C (sans précarité/coup de
  // pouce). prix_unitaire = TAUX €/cumac. type='b2b' pour la distinction + filtre wizard.
  const [showDealB2BDialog, setShowDealB2BDialog] = useState(false);
  const [dealB2BForm, setDealB2BForm] = useState({
    nom: "", delegataire_id: "", delegataire_nom: "", fiche_cee: "BAT-TH-125",
    prix_unitaire: 0, date_debut: "", date_fin: "", type: "b2b", actif: true,
    phrase: "La présente offre comprend une prime de {montant} offerte par {delegataire} (SIREN {siren}) dans le cadre du dispositif des Certificats d'Économies d'Énergie (CEE).",
  });
  const saveDealB2B = async () => {
    setLoading(true);
    try {
      const deleg = (delegataires || []).find(d => (d.id || "") === dealB2BForm.delegataire_id);
      const payload = { ...dealB2BForm, delegataire_nom: deleg?.nom || dealB2BForm.delegataire_nom || "", type: "b2b" };
      const method = dealB2BForm.id ? "PUT" : "POST";
      const url = dealB2BForm.id ? `${API}/api/metier/aides/deals/${dealB2BForm.id}` : `${API}/api/metier/aides/deals`;
      // FIX : getAuthHeaders() n'existe PAS dans AidesModule (seulement dans des sous-composants)
      // → ReferenceError → 'Erreur lors de la sauvegarde' systématique. Headers inline (comme saveDeal B2C).
      const token = localStorage.getItem('session_token');
      const headers = {
        ...enteteTenant(tenantId), "Content-Type": "application/json",
        ...(token && { Authorization: `Bearer ${token}` }),
      };
      let res;
      try {
        res = await fetch(url, { method, headers, body: JSON.stringify(payload), credentials: "include" });
      } catch (netErr) {
        console.error("[deal B2B] échec réseau:", netErr);
        toast.error("Échec réseau — le serveur n'a pas été joint."); return;
      }
      if (res.ok) {
        toast.success(dealB2BForm.id ? "Deal B2B mis à jour" : "Deal B2B créé");
        setShowDealB2BDialog(false); loadDeals();
      } else {
        let detail = "";
        try { detail = (await res.json())?.detail || ""; } catch { /* noop */ }
        toast.error(`Erreur serveur (${res.status})${detail ? " : " + detail : ""}`);
      }
    } finally { setLoading(false); }
  };
  const [showFicheDialog, setShowFicheDialog] = useState(false);
  const [showFicheDetailDialog, setShowFicheDetailDialog] = useState(false);
  const [delegataireForm, setDelegataireForm] = useState({
    nom: "",
    siren: "",
    siret: "",
    adresse: "",
    forme_juridique: "",
    code_naf: "",
    tva_intracommunautaire: "",
    capital_social: 0,
    contact_nom: "",
    contact_email: "",
    contact_telephone: "",
    prix_kwh_cumac: 0.005,
    actif: true
  });
  const [dealForm, setDealForm] = useState({
    nom: "",
    delegataire_id: "",
    fiche_cee: "",
    type_precarite: "classic", // grand_precaire, precaire, classic
    prix_unitaire: 0,
    coup_de_pouce: false,
    bonus_coup_pouce: 0,
    date_debut: "",
    date_fin: "",
    actif: true
  });
  const [ficheForm, setFicheForm] = useState({
    code: "",
    nom: "",
    categorie: "Chauffage",
    kwh_cumac_base: 0,
    kwh_cumac_precaire: 0,
    kwh_cumac_grand_precaire: 0,
    conditions: "",
    // 🔴 LE DESCRIPTIF DES TRAVAUX — ce que le bénéficiaire lit sur le devis.
    // ⚠️ Mesuré le 2026-09-18 : le gabarit OMV imprimait le LIBELLÉ de la fiche
    // à la place d'un descriptif (« VMC simple flux (tertiaire) — fiche
    // BAT-TH-125 »), et aucune des 4 fiches OMV ne portait de texte métier.
    // 🔑 Le référentiel d'une opération ⛔ ne peut pas se réduire à un code et
    // un libellé : il porte SON CONTENU DOCUMENTAIRE. On le saisit ICI, UNE
    // FOIS, ⛔ plutôt que de corriger chaque devis.
    description_travaux: "",
    url_ademe: "",
    actif: true
  });

  // États MPR - Opérations
  const [operationsMPR, setOperationsMPR] = useState([]);
  const [forfaitsMPRLoading, setForfaitsMPRLoading] = useState(false);
  const [showOperationMPRDialog, setShowOperationMPRDialog] = useState(false);
  const [operationMPRForm, setOperationMPRForm] = useState({
    code: "",
    nom: "",
    categorie: "Chauffage",
    plafond_bleu: 0,
    plafond_jaune: 0,
    plafond_violet: 0,
    plafond_rose: 0,
    forfait_bleu: 0,
    forfait_jaune: 0,
    forfait_violet: 0,
    forfait_rose: 0,
    conditions: "",
    actif: true
  });

  // États Barèmes MPR éditables
  const [baremesMPR, setBaremesMPR] = useState(BAREMES_MPR_DEFAULT);
  const [editingBaremes, setEditingBaremes] = useState(false);
  const [savingBaremes, setSavingBaremes] = useState(false);

  // Feature flags aides
  const [featureFlags, setFeatureFlags] = useState({ mpa_enabled: false, mpr_enabled: true, cee_enabled: true });

  // Charger les feature flags
  const loadFeatureFlags = async () => {
    try {
      const token = localStorage.getItem("session_token");
      const headers = { 
        ...enteteTenant(tenantId),
        ...(token && { "Authorization": `Bearer ${token}` })
      };
      const res = await fetch(`${API}/api/metier/aides/feature-flags`, { headers });
      if (res.ok) {
        const data = await res.json();
        setFeatureFlags(data.flags || { mpa_enabled: false, mpr_enabled: true, cee_enabled: true });
      }
    } catch (error) {
      console.error("Erreur chargement feature flags:", error);
    }
  };

  // Charger les données
  useEffect(() => {
    loadFeatureFlags();
    loadDelegataires();
    loadDeals();
    loadFichesCEE();
    loadOperationsMPR();
    loadBaremesMPR();
  }, [tenantId]);

  // Charger les barèmes MPR depuis le backend
  const loadBaremesMPR = async () => {
    try {
      const token = localStorage.getItem("session_token");
      const headers = { 
        ...enteteTenant(tenantId),
        ...(token && { "Authorization": `Bearer ${token}` })
      };
      const res = await fetch(`${API}/api/metier/aides/baremes-mpr`, { headers });
      if (res.ok) {
        const data = await res.json();
        if (data.baremes) {
          setBaremesMPR(data.baremes);
        }
      }
    } catch (error) {
      console.error("Erreur chargement barèmes:", error);
    }
  };

  // Sauvegarder les barèmes MPR
  const saveBaremesMPR = async () => {
    setSavingBaremes(true);
    try {
      const token = localStorage.getItem("session_token");
      const headers = { 
        ...enteteTenant(tenantId),
        "Content-Type": "application/json",
        ...(token && { "Authorization": `Bearer ${token}` })
      };
      const res = await fetch(`${API}/api/metier/aides/baremes-mpr`, {
        method: "PUT",
        headers,
        body: JSON.stringify({ baremes: baremesMPR })
      });
      if (res.ok) {
        toast.success("Barèmes MPR enregistrés");
        setEditingBaremes(false);
      } else {
        toast.error("Erreur lors de l'enregistrement");
      }
    } catch (error) {
      toast.error("Erreur lors de l'enregistrement");
    } finally {
      setSavingBaremes(false);
    }
  };

  // Mettre à jour une valeur de barème
  const updateBareme = (region, nbPersonnes, couleur, value) => {
    setBaremesMPR(prev => ({
      ...prev,
      [region]: {
        ...prev[region],
        [nbPersonnes]: {
          ...prev[region][nbPersonnes],
          [couleur]: parseInt(value) || 0
        }
      }
    }));
  };

  const loadDelegataires = async () => {
    try {
      const headers = { ...enteteTenant(tenantId) };
      const res = await fetch(`${API}/api/metier/aides/delegataires`, { headers });
      if (res.ok) {
        setDelegataires(await res.json());
      }
    } catch (error) {
      console.error("Erreur chargement délégataires:", error);
    }
  };

  const loadDeals = async () => {
    try {
      const headers = { ...enteteTenant(tenantId) };
      const res = await fetch(`${API}/api/metier/aides/deals`, { headers });
      if (res.ok) {
        setDeals(await res.json());
      }
    } catch (error) {
      console.error("Erreur chargement deals:", error);
    }
  };

  const loadFichesCEE = async () => {
    try {
      const headers = { ...enteteTenant(tenantId) };
      const res = await fetch(`${API}/api/metier/aides/fiches-cee`, { headers });
      if (res.ok) {
        const data = await res.json();
        setFichesCEE(data.length > 0 ? data : FICHES_CEE_ADEME.map(f => ({ ...f, actif: true, url_ademe: getAdemeUrl(f.code) })));
      } else {
        // Charger les fiches par défaut
        setFichesCEE(FICHES_CEE_ADEME.map(f => ({ ...f, actif: true, url_ademe: getAdemeUrl(f.code) })));
      }
    } catch (error) {
      console.error("Erreur chargement fiches CEE:", error);
      setFichesCEE(FICHES_CEE_ADEME.map(f => ({ ...f, actif: true, url_ademe: getAdemeUrl(f.code) })));
    }
  };

  const loadOperationsMPR = async () => {
    try {
      setForfaitsMPRLoading(true);
      const headers = { ...enteteTenant(tenantId) };
      const res = await fetch(`${API}/api/metier/aides/operations-mpr`, { headers });
      if (res.ok) {
        setOperationsMPR(await res.json());
      }
    } catch (error) {
      console.error("Erreur chargement opérations MPR:", error);
    } finally {
      setForfaitsMPRLoading(false);
    }
  };

  // URL vers les fiches ADEME officielles
  const getAdemeUrl = (code) => {
    // Format: BAR-TH-171 -> bar-th-171
    const codeFormatted = code.toLowerCase();
    return `https://www.ecologie.gouv.fr/sites/default/files/documents/${codeFormatted}.pdf`;
  };

  // Filtrer les délégataires
  const filteredDelegataires = delegataires.filter(d => 
    searchDelegataire === "" || 
    d.nom?.toLowerCase().includes(searchDelegataire.toLowerCase()) ||
    d.siren?.includes(searchDelegataire)
  );

  // Filtrer les fiches CEE
  const filteredFiches = fichesCEE.filter(f =>
    searchFiche === "" ||
    f.code?.toLowerCase().includes(searchFiche.toLowerCase()) ||
    f.nom?.toLowerCase().includes(searchFiche.toLowerCase()) ||
    f.categorie?.toLowerCase().includes(searchFiche.toLowerCase())
  );

  // Sauvegarder un délégataire
  const saveDelegataire = async () => {
    try {
      setLoading(true);
      const headers = { 
        ...enteteTenant(tenantId),
        "Content-Type": "application/json"
      };
      const method = delegataireForm.id ? "PUT" : "POST";
      const url = delegataireForm.id 
        ? `${API}/api/metier/aides/delegataires/${delegataireForm.id}`
        : `${API}/api/metier/aides/delegataires`;
      
      const res = await fetch(url, {
        method,
        headers,
        body: JSON.stringify(delegataireForm)
      });
      
      if (res.ok) {
        toast.success(delegataireForm.id ? "Délégataire mis à jour" : "Délégataire créé");
        setShowDelegataireDialog(false);
        loadDelegataires();
      } else {
        toast.error("Erreur lors de la sauvegarde");
      }
    } catch (error) {
      console.error("Erreur:", error);
      toast.error("Erreur lors de la sauvegarde");
    } finally {
      setLoading(false);
    }
  };

  // Archiver/Désarchiver un délégataire
  const toggleDelegataireStatus = async (delegataire) => {
    try {
      const headers = { 
        ...enteteTenant(tenantId),
        "Content-Type": "application/json"
      };
      const res = await fetch(`${API}/api/metier/aides/delegataires/${delegataire.id}`, {
        method: "PUT",
        headers,
        body: JSON.stringify({ ...delegataire, actif: !delegataire.actif })
      });
      
      if (res.ok) {
        toast.success(delegataire.actif ? "Délégataire désactivé" : "Délégataire activé");
        loadDelegataires();
      }
    } catch (error) {
      toast.error("Erreur lors de la mise à jour");
    }
  };

  // Supprimer définitivement un délégataire (DELETE /aides/delegataires/{id})
  const deleteDelegataire = async (delegataire) => {
    if (!confirm(`Supprimer définitivement le délégataire "${delegataire.nom}" ?`)) return;
    try {
      const headers = { ...enteteTenant(tenantId) };
      const res = await fetch(`${API}/api/metier/aides/delegataires/${delegataire.id}`, {
        method: "DELETE",
        headers
      });
      if (res.ok) {
        toast.success("Délégataire supprimé");
        loadDelegataires();
      } else {
        toast.error("Erreur lors de la suppression");
      }
    } catch (error) {
      console.error("Erreur suppression délégataire:", error);
      toast.error("Erreur lors de la suppression");
    }
  };

  // Supprimer définitivement un deal (DELETE /aides/deals/{id})
  const deleteDeal = async (deal) => {
    if (!confirm(`Supprimer définitivement le deal "${deal.nom}" ?`)) return;
    try {
      const headers = { ...enteteTenant(tenantId) };
      const res = await fetch(`${API}/api/metier/aides/deals/${deal.id}`, {
        method: "DELETE",
        headers
      });
      if (res.ok) {
        toast.success("Deal supprimé");
        loadDeals();
      } else {
        toast.error("Erreur lors de la suppression");
      }
    } catch (error) {
      console.error("Erreur suppression deal:", error);
      toast.error("Erreur lors de la suppression");
    }
  };

  // Sauvegarder un deal
  const saveDeal = async () => {
    try {
      setLoading(true);
      const headers = { 
        ...enteteTenant(tenantId),
        "Content-Type": "application/json"
      };
      const method = dealForm.id ? "PUT" : "POST";
      const url = dealForm.id 
        ? `${API}/api/metier/aides/deals/${dealForm.id}`
        : `${API}/api/metier/aides/deals`;
      
      const res = await fetch(url, {
        method,
        headers,
        body: JSON.stringify(dealForm)
      });
      
      if (res.ok) {
        toast.success(dealForm.id ? "Deal mis à jour" : "Deal créé");
        setShowDealDialog(false);
        loadDeals();
      } else {
        toast.error("Erreur lors de la sauvegarde");
      }
    } catch (error) {
      console.error("Erreur:", error);
      toast.error("Erreur lors de la sauvegarde");
    } finally {
      setLoading(false);
    }
  };

  // Sauvegarder une fiche CEE
  const saveFicheCEE = async () => {
    try {
      setLoading(true);
      const headers = { 
        ...enteteTenant(tenantId),
        "Content-Type": "application/json"
      };
      const method = ficheForm.id ? "PUT" : "POST";
      const url = ficheForm.id 
        ? `${API}/api/metier/aides/fiches-cee/${ficheForm.id}`
        : `${API}/api/metier/aides/fiches-cee`;
      
      // Ajouter l'URL ADEME automatiquement
      const dataToSave = {
        ...ficheForm,
        url_ademe: ficheForm.url_ademe || getAdemeUrl(ficheForm.code)
      };
      
      const res = await fetch(url, {
        method,
        headers,
        body: JSON.stringify(dataToSave)
      });
      
      if (res.ok) {
        toast.success(ficheForm.id ? "Fiche CEE mise à jour" : "Fiche CEE créée");
        setShowFicheDialog(false);
        loadFichesCEE();
      } else {
        toast.error("Erreur lors de la sauvegarde");
      }
    } catch (error) {
      console.error("Erreur:", error);
      toast.error("Erreur lors de la sauvegarde");
    } finally {
      setLoading(false);
    }
  };

  // Archiver/Désarchiver une fiche CEE
  const toggleFicheStatus = async (fiche) => {
    try {
      const headers = { 
        ...enteteTenant(tenantId),
        "Content-Type": "application/json"
      };
      const res = await fetch(`${API}/api/metier/aides/fiches-cee/${fiche.id || fiche.code}`, {
        method: "PUT",
        headers,
        body: JSON.stringify({ ...fiche, actif: !fiche.actif })
      });
      
      if (res.ok) {
        toast.success(fiche.actif ? "Fiche désactivée" : "Fiche activée");
        loadFichesCEE();
      }
    } catch (error) {
      toast.error("Erreur lors de la mise à jour");
    }
  };

  // Sauvegarder une opération MPR
  const saveOperationMPR = async () => {
    try {
      setLoading(true);
      const headers = { 
        ...enteteTenant(tenantId),
        "Content-Type": "application/json"
      };
      const method = operationMPRForm.id ? "PUT" : "POST";
      const url = operationMPRForm.id 
        ? `${API}/api/metier/aides/operations-mpr/${operationMPRForm.id}`
        : `${API}/api/metier/aides/operations-mpr`;
      
      const res = await fetch(url, {
        method,
        headers,
        body: JSON.stringify(operationMPRForm)
      });
      
      if (res.ok) {
        toast.success(operationMPRForm.id ? "Opération mise à jour" : "Opération créée");
        setShowOperationMPRDialog(false);
        loadOperationsMPR();
      } else {
        toast.error("Erreur lors de la sauvegarde");
      }
    } catch (error) {
      console.error("Erreur:", error);
      toast.error("Erreur lors de la sauvegarde");
    } finally {
      setLoading(false);
    }
  };

  // Supprimer une opération MPR
  const deleteOperationMPR = async (id) => {
    if (!confirm("Supprimer cet équipement ?")) return;
    
    try {
      const headers = { ...enteteTenant(tenantId) };
      const res = await fetch(`${API}/api/metier/aides/operations-mpr/${id}`, {
        method: "DELETE",
        headers
      });
      
      if (res.ok) {
        toast.success("Équipement supprimé");
        loadOperationsMPR();
      } else {
        toast.error("Erreur lors de la suppression");
      }
    } catch (error) {
      console.error("Erreur:", error);
      toast.error("Erreur lors de la suppression");
    }
  };

  // Initialiser avec le barème officiel ANAH
  const initDefaultOperationsMPR = async () => {
    if (!confirm("Réinitialiser avec le barème officiel ANAH 2026 ?\nCela remplacera vos personnalisations.")) return;
    
    try {
      setForfaitsMPRLoading(true);
      const headers = { 
        ...enteteTenant(tenantId),
        "Content-Type": "application/json"
      };
      
      // Supprimer les personnalisations existantes
      await fetch(`${API}/api/metier/aides/operations-mpr/reset`, {
        method: "POST",
        headers
      });
      
      // Recharger pour obtenir les valeurs par défaut
      await loadOperationsMPR();
      toast.success("Forfaits réinitialisés avec le barème ANAH 2026");
    } catch (error) {
      console.error("Erreur:", error);
      toast.error("Erreur lors de la réinitialisation");
    } finally {
      setForfaitsMPRLoading(false);
    }
  };

  // Calculer la couleur MPR
  const getCouleurMPR = (rfr, nbPersonnes, region) => {
    const bareme = region === "idf" ? BAREMES_MPR.idf : BAREMES_MPR.province;
    const nb = Math.min(nbPersonnes, 5);
    const seuils = bareme[nb];
    
    // Ajout par personne supplémentaire
    let seuilBleu = seuils.bleu;
    let seuilJaune = seuils.jaune;
    let seuilViolet = seuils.violet;
    
    if (nbPersonnes > 5) {
      const extra = nbPersonnes - 5;
      seuilBleu += extra * bareme.parPersonne.bleu;
      seuilJaune += extra * bareme.parPersonne.jaune;
      seuilViolet += extra * bareme.parPersonne.violet;
    }
    
    if (rfr <= seuilBleu) return "bleu";
    if (rfr <= seuilJaune) return "jaune";
    if (rfr <= seuilViolet) return "violet";
    return "rose";
  };

  // Simuler les aides via API backend
  const runSimulation = async () => {
    if (!simulationData.rfr || !simulationData.operation || !simulationData.cout_travaux) {
      toast.error("Veuillez remplir tous les champs obligatoires");
      return;
    }

    const rfr = parseFloat(simulationData.rfr);
    const coutTravaux = parseFloat(simulationData.cout_travaux);
    
    setLoading(true);
    
    try {
      // Appeler l'API backend pour le calcul
      const headers = { 
        ...enteteTenant(tenantId),
        "Content-Type": "application/json"
      };
      
      const requestBody = {
        mode: "simulation",
        client_rfr: rfr,
        client_nb_personnes: simulationData.nb_personnes,
        client_region: simulationData.region,
        logement_type: simulationData.type_logement,
        logement_surface: parseFloat(simulationData.surface) || 100,
        logement_code_postal: simulationData.code_postal || "69001",
        operation_code: simulationData.operation,
        // Ajouter des paramètres optionnels pour un meilleur calcul CEE
        produit_etas: parseFloat(simulationData.etas) || null,
        produit_cop: parseFloat(simulationData.cop) || null
      };
      
      const res = await fetch(`${API}/api/metier/aides/simuler`, {
        method: "POST",
        headers,
        body: JSON.stringify(requestBody)
      });
      
      if (res.ok) {
        const apiResult = await res.json();
        
        // Trouver la fiche CEE pour l'affichage
        const ficheCEE = fichesCEE.find(f => f.code === simulationData.operation) || 
                         FICHES_CEE_ADEME.find(f => f.code === simulationData.operation);
        
        // Mapper le résultat API vers le format attendu par l'affichage
        const totalAides = apiResult.total_aides || 0;
        const resteACharge = Math.max(0, coutTravaux - totalAides);
        const tauxCouverture = coutTravaux > 0 ? (totalAides / coutTravaux * 100).toFixed(1) : 0;
        
        setSimulationResult({
          couleur: apiResult.mpr_couleur,
          rfr,
          nbPersonnes: simulationData.nb_personnes,
          region: simulationData.region,
          operation: ficheCEE,
          coutTravaux,
          mpr: {
            montant: apiResult.mpr_montant || 0,
            plafond: apiResult.mpr_montant || 0,
            taux: apiResult.mpr_couleur === "bleu" ? 90 : 
                  apiResult.mpr_couleur === "jaune" ? 75 : 
                  apiResult.mpr_couleur === "violet" ? 60 : 40,
            version: apiResult.mpr_bareme_version
          },
          cee: {
            montant: apiResult.cee_montant || 0,
            kwhCumac: apiResult.cee_kwh_cumac || 0,
            prixKwh: apiResult.cee_valo || 0.007,
            version: apiResult.cee_fiche_version
          },
          coupDePouce: 0, // TODO: ajouter dans le backend
          totalAides,
          resteACharge,
          tauxCouverture,
          zoneClimatique: apiResult.zone_climatique,
          mentionMpr: apiResult.mention_mpr,
          mentionCee: apiResult.mention_cee,
          details: apiResult.details_calcul
        });
        
        toast.success("Simulation effectuée via le moteur de calcul");
      } else {
        const err = await res.json();
        toast.error(err.detail || "Erreur lors de la simulation");
        // Fallback vers le calcul local si l'API échoue
        runSimulationLocal();
      }
    } catch (error) {
      console.error("Erreur simulation API:", error);
      toast.error("Erreur de connexion - calcul local utilisé");
      runSimulationLocal();
    } finally {
      setLoading(false);
    }
  };
  
  // Calcul local de secours (fallback)
  const runSimulationLocal = () => {
    const rfr = parseFloat(simulationData.rfr);
    const coutTravaux = parseFloat(simulationData.cout_travaux);
    const couleur = getCouleurMPR(rfr, simulationData.nb_personnes, simulationData.region);
    
    const ficheCEE = fichesCEE.find(f => f.code === simulationData.operation) ||
                     FICHES_CEE_ADEME.find(f => f.code === simulationData.operation);
    
    // Forfaits MPR 2025
    const forfaitsMPR = {
      "BAR-TH-171": { bleu: 5000, jaune: 4000, violet: 3000, rose: 0 },
      "BAR-TH-148": { bleu: 1200, jaune: 800, violet: 400, rose: 0 },
      "BAR-TH-129": { bleu: 0, jaune: 0, violet: 0, rose: 0 },
      "BAR-TH-104": { bleu: 5000, jaune: 4000, violet: 3000, rose: 0 },
      "BAR-TH-106": { bleu: 1200, jaune: 800, violet: 0, rose: 0 },
      default: { bleu: 2000, jaune: 1500, violet: 1000, rose: 0 }
    };
    
    const forfait = forfaitsMPR[simulationData.operation] || forfaitsMPR.default;
    const mprMontant = forfait[couleur] || 0;
    
    // CEE simplifié
    const ceeKwh = ficheCEE?.kwh_cumac || 0;
    const ceeMontant = ceeKwh * 0.007;
    
    const totalAides = mprMontant + ceeMontant;
    const resteACharge = Math.max(0, coutTravaux - totalAides);
    const tauxCouverture = coutTravaux > 0 ? (totalAides / coutTravaux * 100).toFixed(1) : 0;

    setSimulationResult({
      couleur,
      rfr,
      nbPersonnes: simulationData.nb_personnes,
      region: simulationData.region,
      operation: ficheCEE,
      coutTravaux,
      mpr: {
        montant: mprMontant,
        plafond: mprMontant,
        taux: couleur === "bleu" ? 90 : couleur === "jaune" ? 75 : couleur === "violet" ? 60 : 40
      },
      cee: {
        montant: ceeMontant,
        kwhCumac: ceeKwh,
        prixKwh: 0.007
      },
      coupDePouce: 0,
      totalAides,
      resteACharge,
      tauxCouverture
    });
  };

  // États pour la recherche d'entreprise
  const [searchEntreprise, setSearchEntreprise] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);

  // Recherche d'entreprise via API gouv.fr (gratuite)
  const searchEntrepriseApi = async (query) => {
    if (!query || query.length < 3) {
      setSearchResults([]);
      setShowSearchResults(false);
      return;
    }
    
    try {
      setSearchLoading(true);
      // API recherche-entreprises.api.gouv.fr (gratuite, sans authentification)
      const res = await fetch(
        `https://recherche-entreprises.api.gouv.fr/search?q=${encodeURIComponent(query)}&page=1&per_page=10`
      );
      
      if (res.ok) {
        const data = await res.json();
        setSearchResults(data.results || []);
        setShowSearchResults(true);
      } else {
        setSearchResults([]);
      }
    } catch (error) {
      console.error("Erreur recherche entreprise:", error);
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  };

  // Sélectionner une entreprise depuis les résultats
  const selectEntreprise = (entreprise) => {
    const siege = entreprise.siege || {};
    const adresseComplete = [
      siege.numero_voie,
      siege.type_voie,
      siege.libelle_voie,
      siege.code_postal,
      siege.libelle_commune
    ].filter(Boolean).join(" ");

    setDelegataireForm(prev => ({
      ...prev,
      nom: entreprise.nom_complet || entreprise.nom_raison_sociale || "",
      siren: entreprise.siren || "",
      siret: siege.siret || "",
      adresse: adresseComplete,
      forme_juridique: entreprise.nature_juridique || "",
      code_naf: siege.activite_principale || entreprise.activite_principale || "",
      tva_intracommunautaire: entreprise.siren ? `FR${String(12 + 3 * (parseInt(entreprise.siren) % 97)).padStart(2, '0')}${entreprise.siren}` : "",
      capital_social: entreprise.capital || 0
    }));
    
    setSearchEntreprise(entreprise.nom_complet || "");
    setShowSearchResults(false);
    toast.success(`Entreprise "${entreprise.nom_complet}" sélectionnée`);
  };

  // Debounce pour la recherche
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchEntreprise.length >= 3) {
        searchEntrepriseApi(searchEntreprise);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchEntreprise]);

  // Format monétaire
  const formatMontant = (val) => {
    if (val === null || val === undefined) return "-";
    return new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(val);
  };

  return (
    <div className="p-6 space-y-6" data-testid="aides-module">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg" style={{ background: "var(--oh-accent-bg)" }}>
            <Euro className="w-6 h-6 text-[var(--oh-accent-text)]" />
          </div>
          <div>
            <h1 className="text-2xl font-medium text-[var(--oh-text)]">Module aides</h1>
            <p className="text-sm text-[var(--oh-text-secondary)]">{aidesLabel('aides_sous_titre', showMpr)}</p>
          </div>
        </div>
      </div>

      {/* Onglets */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        {/* §9 responsive : 9 onglets → scroll horizontal INTERNE (jamais de
            débordement de la page) en mobile. */}
        <TabsList className="bg-[var(--oh-bg-elevated)] border border-[var(--oh-border)] max-w-full overflow-x-auto justify-start">
          <TabsTrigger value="simulateur" className="data-[state=active]:bg-[var(--oh-accent-solid)]">
            <Calculator className="w-4 h-4 mr-2" />
            Simulateur d'aides
          </TabsTrigger>
          <TabsTrigger value="cee" className="data-[state=active]:bg-[var(--oh-accent-solid)]">
            <FileText className="w-4 h-4 mr-2" />
            CEE
          </TabsTrigger>
          {/* MaPrimeRénov' + Plafonds Cumul = concepts résidentiels (hors CEE tertiaire) :
              masqués pour OMV (gating omv_france). TEF/climat inchangés. */}
          {!isOmv && (
            <TabsTrigger value="mpr" className="data-[state=active]:bg-[var(--oh-accent-solid)]">
              <Leaf className="w-4 h-4 mr-2" />
              MaPrimeRénov
            </TabsTrigger>
          )}
          {!isOmv && (
            <TabsTrigger value="plafonds" className="data-[state=active]:bg-[var(--oh-accent-solid)]">
              <TrendingUp className="w-4 h-4 mr-2" />
              Plafonds cumul
            </TabsTrigger>
          )}
          <TabsTrigger value="documents" className="data-[state=active]:bg-[var(--oh-accent-solid)]">
            <FileText className="w-4 h-4 mr-2" />
            Documents templates
          </TabsTrigger>
          <TabsTrigger value="variables" className="data-[state=active]:bg-[var(--oh-accent-solid)]">
            <Variable className="w-4 h-4 mr-2" />
            Variables et listes
          </TabsTrigger>
          <TabsTrigger value="sources" className="data-[state=active]:bg-[var(--oh-accent-solid)]">
            <Upload className="w-4 h-4 mr-2" />
            Modèles officiels
          </TabsTrigger>
          <TabsTrigger value="mentions" className="data-[state=active]:bg-[var(--oh-accent-solid)]">
            <ScrollText className="w-4 h-4 mr-2" />
            Mentions obligatoires
          </TabsTrigger>
          {featureFlags.mpa_enabled && (
            <TabsTrigger value="mpa" className="data-[state=active]:bg-[var(--oh-accent-solid)]">
              <Accessibility className="w-4 h-4 mr-2" />
              MaPrimeAdapt'
            </TabsTrigger>
          )}
        </TabsList>

        {/* ============================================== */}
        {/* ONGLET SIMULATEUR */}
        {/* ============================================== */}
        <TabsContent value="simulateur" className="mt-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Formulaire de simulation */}
            <Card className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]">
              <CardHeader>
                <CardTitle className="text-[var(--oh-text)] flex items-center gap-2">
                  <Home className="w-5 h-5 text-[var(--oh-success-text)]" />
                  Simulation d'aides
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Section Logement */}
                <div className="space-y-4">
                  <h4 className="text-sm font-medium text-[var(--oh-accent-text)] border-b border-[var(--oh-border)] pb-2">
                    Logement
                  </h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <label className="text-xs text-[var(--oh-text-secondary)]">Type de logement</label>
                      <Select
                        value={simulationData.type_logement}
                        onValueChange={(v) => setSimulationData({...simulationData, type_logement: v})}
                      >
                        <SelectTrigger className="bg-[var(--oh-bg-surface)] border-[var(--oh-border)]">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {logementTypeOptions.map(o => (
                            <SelectItem key={o.id} value={o.id}>{o.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <label className="text-xs text-[var(--oh-text-secondary)]">Surface (m²)</label>
                      <Input
                        type="number"
                        value={simulationData.surface}
                        onChange={(e) => setSimulationData({...simulationData, surface: e.target.value})}
                        className="bg-[var(--oh-bg-surface)] border-[var(--oh-border)]"
                        placeholder="100"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-xs text-[var(--oh-text-secondary)]">Année construction</label>
                      <Input
                        type="number"
                        value={simulationData.annee_construction}
                        onChange={(e) => setSimulationData({...simulationData, annee_construction: e.target.value})}
                        className="bg-[var(--oh-bg-surface)] border-[var(--oh-border)]"
                        placeholder="1980"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-xs text-[var(--oh-text-secondary)]">DPE actuel</label>
                      <Select
                        value={simulationData.dpe_actuel}
                        onValueChange={(v) => setSimulationData({...simulationData, dpe_actuel: v})}
                      >
                        <SelectTrigger className="bg-[var(--oh-bg-surface)] border-[var(--oh-border)]">
                          <SelectValue placeholder="Sélectionner" />
                        </SelectTrigger>
                        <SelectContent>
                          {["A", "B", "C", "D", "E", "F", "G"].map(dpe => (
                            <SelectItem key={dpe} value={dpe}>{dpe}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <label className="text-xs text-[var(--oh-text-secondary)]">Région *</label>
                      <Select
                        value={simulationData.region}
                        onValueChange={(v) => setSimulationData({...simulationData, region: v})}
                      >
                        <SelectTrigger className="bg-[var(--oh-bg-surface)] border-[var(--oh-border)]">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="idf">Île-de-France</SelectItem>
                          <SelectItem value="province">Province</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <label className="text-xs text-[var(--oh-text-secondary)]">Code postal</label>
                      <Input
                        value={simulationData.code_postal}
                        onChange={(e) => setSimulationData({...simulationData, code_postal: e.target.value})}
                        className="bg-[var(--oh-bg-surface)] border-[var(--oh-border)]"
                        placeholder="75000"
                      />
                    </div>
                  </div>
                </div>

                {/* Section Ménage */}
                <div className="space-y-4">
                  <h4 className="text-sm font-medium text-[var(--oh-accent-text)] border-b border-[var(--oh-border)] pb-2">
                    Ménage
                  </h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <label className="text-xs text-[var(--oh-text-secondary)]">Nombre de personnes *</label>
                      <Select
                        value={String(simulationData.nb_personnes)}
                        onValueChange={(v) => setSimulationData({...simulationData, nb_personnes: parseInt(v)})}
                      >
                        <SelectTrigger className="bg-[var(--oh-bg-surface)] border-[var(--oh-border)]">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {[1,2,3,4,5,6,7,8].map(n => (
                            <SelectItem key={n} value={String(n)}>{n} personne{n > 1 ? "s" : ""}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <label className="text-xs text-[var(--oh-text-secondary)]">Revenu fiscal (RFR) *</label>
                      <div className="relative">
                        <Euro className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--oh-text-secondary)]" />
                        <Input
                          type="number"
                          value={simulationData.rfr}
                          onChange={(e) => setSimulationData({...simulationData, rfr: e.target.value})}
                          className="bg-[var(--oh-bg-surface)] border-[var(--oh-border)] pl-10"
                          placeholder="25000"
                        />
                      </div>
                    </div>
                    <div className="col-span-2 space-y-2">
                      <label className="text-xs text-[var(--oh-text-secondary)]">Statut</label>
                      <Select
                        value={simulationData.statut}
                        onValueChange={(v) => setSimulationData({...simulationData, statut: v})}
                      >
                        <SelectTrigger className="bg-[var(--oh-bg-surface)] border-[var(--oh-border)]">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {occupationOptions.length > 0
                            ? occupationOptions.map(o => <SelectItem key={o.id} value={o.id}>{o.label}</SelectItem>)
                            : <><SelectItem value="proprietaire_occupant">Propriétaire occupant</SelectItem><SelectItem value="proprietaire_bailleur">Propriétaire bailleur</SelectItem><SelectItem value="copropriete">Copropriété</SelectItem></>
                          }
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>

                {/* Section Travaux */}
                <div className="space-y-4">
                  <h4 className="text-sm font-medium text-[var(--oh-accent-text)] border-b border-[var(--oh-border)] pb-2">
                    Travaux envisagés
                  </h4>
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <label className="text-xs text-[var(--oh-text-secondary)]">Type d'opération *</label>
                      <Select
                        value={simulationData.operation}
                        onValueChange={(v) => setSimulationData({...simulationData, operation: v})}
                      >
                        <SelectTrigger className="bg-[var(--oh-bg-surface)] border-[var(--oh-border)]">
                          <SelectValue placeholder="Sélectionner une opération" />
                        </SelectTrigger>
                        <SelectContent>
                          {FICHES_CEE_ADEME.map(f => (
                            <SelectItem key={f.code} value={f.code}>
                              {f.code} - {f.nom}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <label className="text-xs text-[var(--oh-text-secondary)]">Coût estimé des travaux TTC *</label>
                      <div className="relative">
                        <Euro className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--oh-text-secondary)]" />
                        <Input
                          type="number"
                          value={simulationData.cout_travaux}
                          onChange={(e) => setSimulationData({...simulationData, cout_travaux: e.target.value})}
                          className="bg-[var(--oh-bg-surface)] border-[var(--oh-border)] pl-10"
                          placeholder="15000"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <Button
                  onClick={runSimulation}
                  className="w-full bg-[var(--oh-accent-solid)] hover:bg-[var(--oh-accent-solid)]"
                  data-testid="run-simulation-btn"
                >
                  <Calculator className="w-4 h-4 mr-2" />
                  Calculer mes aides
                </Button>
              </CardContent>
            </Card>

            {/* Résultats de simulation */}
            <Card className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]">
              <CardHeader>
                <CardTitle className="text-[var(--oh-text)] flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-[var(--oh-success-text)]" />
                  Résultats de la simulation
                </CardTitle>
              </CardHeader>
              <CardContent>
                {simulationResult ? (
                  <div className="space-y-6">
                    {/* Couleur MPR — #6 : masqué pour un profil CEE-only (pas de MPR). */}
                    {showMpr && (
                    <div className="flex items-center justify-between p-4 bg-[var(--oh-bg-surface)] rounded-lg">
                      <div>
                        <p className="text-sm text-[var(--oh-text-secondary)]">Votre catégorie MPR</p>
                        <div className="flex items-center gap-2 mt-1">
                          <div className="w-4 h-4 rounded-full" style={{ background: COULEURS_MPR[simulationResult.couleur].token }} />
                          <span className="font-medium" style={{ color: COULEURS_MPR[simulationResult.couleur].token }}>
                            {COULEURS_MPR[simulationResult.couleur].label}
                          </span>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-[var(--oh-text-secondary)]">RFR: {formatMontant(simulationResult.rfr)}</p>
                        <p className="text-xs text-[var(--oh-text-secondary)]">{simulationResult.nbPersonnes} pers. - {simulationResult.region === "idf" ? "Île-de-France" : "Province"}</p>
                      </div>
                    </div>
                    )}

                    {/* Détail des aides */}
                    <div className="space-y-3">
                      {/* MPR — #6 : masqué pour un profil CEE-only (pas de MPR). */}
                      {showMpr && (
                      <div className="flex items-center justify-between p-3 bg-[var(--oh-accent-bg)] border border-[var(--oh-accent)] rounded-lg">
                        <div className="flex items-center gap-2">
                          <Leaf className="w-5 h-5 text-[var(--oh-accent-text)]" />
                          <div>
                            <p className="text-[var(--oh-text)] font-medium">MaPrimeRénov'</p>
                            <p className="text-xs text-[var(--oh-text-secondary)]">Taux {simulationResult.mpr.taux}% - Plafond {formatMontant(simulationResult.mpr.plafond)}</p>
                          </div>
                        </div>
                        <span className="text-xl font-medium text-[var(--oh-accent-text)]">
                          {formatMontant(simulationResult.mpr.montant)}
                        </span>
                      </div>
                      )}

                      {/* CEE */}
                      <div className="flex items-center justify-between p-3 bg-[var(--oh-accent-bg)] border border-[var(--oh-accent)] rounded-lg">
                        <div className="flex items-center gap-2">
                          <FileText className="w-5 h-5 text-[var(--oh-accent-text)]" />
                          <div>
                            <p className="text-[var(--oh-text)] font-medium">Prime CEE</p>
                            <p className="text-xs text-[var(--oh-text-secondary)]">{simulationResult.cee.kwhCumac.toLocaleString("fr-FR")} kWh cumac</p>
                          </div>
                        </div>
                        <span className="text-xl font-medium text-[var(--oh-accent-text)]">
                          {formatMontant(simulationResult.cee.montant)}
                        </span>
                      </div>

                      {/* Coup de pouce */}
                      {simulationResult.coupDePouce > 0 && (
                        <div className="flex items-center justify-between p-3 bg-[var(--oh-info-bg)] border border-[var(--oh-info-border)] rounded-lg">
                          <div className="flex items-center gap-2">
                            <TrendingUp className="w-5 h-5 text-[var(--oh-info-text)]" />
                            <div>
                              <p className="text-[var(--oh-text)] font-medium">Coup de Pouce</p>
                              <p className="text-xs text-[var(--oh-text-secondary)]">Bonus chauffage décarbonné</p>
                            </div>
                          </div>
                          <span className="text-xl font-medium text-[var(--oh-info-text)]">
                            {formatMontant(simulationResult.coupDePouce)}
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Total */}
                    <div className="border-t border-[var(--oh-border)] pt-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-[var(--oh-text-secondary)]">Coût des travaux</span>
                        <span className="text-[var(--oh-text)]">{formatMontant(simulationResult.coutTravaux)}</span>
                      </div>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-[var(--oh-text-secondary)]">Total des aides</span>
                        <span className="text-[var(--oh-success-text)] font-medium">- {formatMontant(simulationResult.totalAides)}</span>
                      </div>
                      <div className="flex items-center justify-between p-3 bg-[var(--oh-success-bg)] border border-[var(--oh-success-border)] rounded-lg">
                        <span className="text-[var(--oh-text)] font-medium">Reste à charge</span>
                        <div className="text-right">
                          <span className="text-2xl font-medium text-[var(--oh-success-text)]">
                            {formatMontant(simulationResult.resteACharge)}
                          </span>
                          <p className="text-xs text-[var(--oh-text-secondary)]">{nombreFR(simulationResult.tauxCouverture, 1)}% financé</p>
                        </div>
                      </div>
                    </div>

                    <p className="text-xs text-[var(--oh-text-secondary)] italic">
                      * Estimation indicative. Les montants réels peuvent varier selon votre situation et les conditions d'éligibilité.
                    </p>
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <Calculator className="w-12 h-12 text-[var(--oh-text-secondary)] mx-auto mb-4" />
                    <p className="text-[var(--oh-text-secondary)]">Remplissez le formulaire pour obtenir une estimation</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* ============================================== */}
        {/* ONGLET CEE */}
        {/* ============================================== */}
        <TabsContent value="cee" className="mt-6 space-y-6">
          {/* Section Délégataires */}
          <Card className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]">
            <CardHeader className="flex flex-col gap-2 md:flex-row md:gap-0 items-center justify-between">
              <CardTitle className="text-[var(--oh-text)] flex items-center gap-2">
                <Building2 className="w-5 h-5 text-[var(--oh-accent-text)]" />
                Délégataires / Pollueurs
              </CardTitle>
              <div className="flex items-center gap-3">
                {/* Recherche */}
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--oh-text-secondary)]" />
                  <Input
                    placeholder="Rechercher par nom ou SIREN..."
                    value={searchDelegataire}
                    onChange={(e) => setSearchDelegataire(e.target.value)}
                    className="bg-[var(--oh-bg-surface)] border-[var(--oh-border)] pl-10 w-64"
                  />
                </div>
                <Button 
                  onClick={() => {
                    setDelegataireForm({ nom: "", siren: "", adresse: "", contact_nom: "", contact_email: "", contact_telephone: "", prix_kwh_cumac: 0.005, actif: true });
                    setShowDelegataireDialog(true);
                  }}
                  className="bg-[var(--oh-accent-solid)] hover:bg-[var(--oh-accent-solid)]"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Nouveau délégataire
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {filteredDelegataires.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow className="border-[var(--oh-border)]">
                      <TableHead className="text-[var(--oh-text-secondary)]">Nom</TableHead>
                      <TableHead className="text-[var(--oh-text-secondary)]">SIREN</TableHead>
                      <TableHead className="text-[var(--oh-text-secondary)]">Contact</TableHead>
                      <TableHead className="text-[var(--oh-text-secondary)] text-right">Prix kWh cumac</TableHead>
                      <TableHead className="text-[var(--oh-text-secondary)] text-center">Statut</TableHead>
                      <TableHead className="text-[var(--oh-text-secondary)] text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredDelegataires.map((d) => (
                      <TableRow key={d.id} className={`border-[var(--oh-border)] ${!d.actif ? 'opacity-50' : ''}`}>
                        <TableCell className="text-[var(--oh-text)] font-medium">{d.nom}</TableCell>
                        <TableCell className="text-[var(--oh-text-secondary)] font-mono">{d.siren || "-"}</TableCell>
                        <TableCell className="text-[var(--oh-text-secondary)]">
                          <div className="flex flex-col">
                            <span>{d.contact_nom || "-"}</span>
                            {d.contact_email && <span className="text-xs text-[var(--oh-text-secondary)]">{d.contact_email}</span>}
                          </div>
                        </TableCell>
                        <TableCell className="text-right text-[var(--oh-success-text)] font-mono">{nombreFR(d.prix_kwh_cumac, 4)} €</TableCell>
                        <TableCell className="text-center">
                          <Badge className={d.actif ? "bg-[var(--oh-success-bg)] text-[var(--oh-success-text)]" : "bg-[var(--oh-bg-elevated)]/20 text-[var(--oh-text-secondary)]"}>
                            {d.actif ? "Actif" : "Inactif"}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setSelectedDelegataire(d);
                                // Afficher le détail
                              }}
                              title="Voir le détail"
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setDelegataireForm(d);
                                setShowDelegataireDialog(true);
                              }}
                              title="Modifier"
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => toggleDelegataireStatus(d)}
                              title={d.actif ? "Désactiver" : "Activer"}
                            >
                              {d.actif ? <Trash2 className="w-4 h-4 text-[var(--oh-error-text)]" /> : <CheckCircle className="w-4 h-4 text-[var(--oh-success-text)]" />}
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => deleteDelegataire(d)}
                              title="Supprimer définitivement"
                              aria-label="Supprimer le délégataire"
                            >
                              <X className="w-4 h-4 text-[var(--oh-error-text)]" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="text-center py-8">
                  <Building2 className="w-12 h-12 text-[var(--oh-text-secondary)] mx-auto mb-4" />
                  <p className="text-[var(--oh-text-secondary)]">
                    {searchDelegataire ? "Aucun délégataire trouvé" : "Aucun délégataire configuré"}
                  </p>
                  <p className="text-sm text-[var(--oh-text-secondary)]">
                    {searchDelegataire ? "Essayez une autre recherche" : "Ajoutez vos partenaires CEE pour commencer"}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Section Deals CEE */}
          <Card className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]">
            <CardHeader className="flex flex-col gap-2 md:flex-row md:gap-0 items-center justify-between">
              <CardTitle className="text-[var(--oh-text)] flex items-center gap-2">
                <FileText className="w-5 h-5 text-[var(--oh-success-text)]" />
                Deals CEE
              </CardTitle>
              <div className="flex gap-2">
                {isOmv && (
                  <Button
                    variant="outline"
                    onClick={() => {
                      setDealB2BForm({ nom: "", delegataire_id: "", delegataire_nom: "", fiche_cee: "BAT-TH-125", prix_unitaire: 0, date_debut: "", date_fin: "", type: "b2b", actif: true });
                      setShowDealB2BDialog(true);
                    }}
                    className="border-[var(--oh-accent)] text-[var(--oh-accent-text)]" data-testid="nouveau-deal-b2b"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Nouveau deal B2B
                  </Button>
                )}
                <Button
                  onClick={() => {
                    setDealForm({ nom: "", delegataire_id: "", fiche_cee: "", type_precarite: "classic", prix_unitaire: 0, coup_de_pouce: false, bonus_coup_pouce: 0, date_debut: "", date_fin: "", actif: true });
                    setShowDealDialog(true);
                  }}
                  className="bg-[var(--oh-accent-solid)] hover:bg-[var(--oh-accent-solid)]"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Nouveau deal
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {deals.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow className="border-[var(--oh-border)]">
                      <TableHead className="text-[var(--oh-text-secondary)]">Nom du deal</TableHead>
                      <TableHead className="text-[var(--oh-text-secondary)]">Délégataire</TableHead>
                      <TableHead className="text-[var(--oh-text-secondary)]">Fiche CEE</TableHead>
                      <TableHead className="text-[var(--oh-text-secondary)] text-center">Type</TableHead>
                      <TableHead className="text-[var(--oh-text-secondary)] text-right">Prix unitaire</TableHead>
                      <TableHead className="text-[var(--oh-text-secondary)] text-center">Coup de Pouce</TableHead>
                      <TableHead className="text-[var(--oh-text-secondary)] text-center">Statut</TableHead>
                      <TableHead className="text-[var(--oh-text-secondary)] text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {deals.map((d) => (
                      <TableRow key={d.id} className={`border-[var(--oh-border)] ${!d.actif ? 'opacity-50' : ''}`}>
                        <TableCell className="text-[var(--oh-text)] font-medium">{d.nom}</TableCell>
                        <TableCell className="text-[var(--oh-text-secondary)]">
                          {delegataires.find(del => del.id === d.delegataire_id)?.nom || "-"}
                        </TableCell>
                        <TableCell className="text-[var(--oh-accent-text)] font-mono">{d.fiche_cee}</TableCell>
                        <TableCell className="text-center">
                          <Badge className={
                            d.type_precarite === "grand_precaire" ? "bg-[var(--oh-accent-bg)] text-[var(--oh-accent-text)]" :
                            d.type_precarite === "precaire" ? "bg-[var(--oh-info-bg)] text-[var(--oh-info-text)]" :
                            "bg-[var(--oh-bg-elevated)]/20 text-[var(--oh-text-muted)]"
                          }>
                            {d.type_precarite === "grand_precaire" ? "Grand précaire" :
                             d.type_precarite === "precaire" ? "Précaire" : "Classic"}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right text-[var(--oh-success-text)] font-mono">{formatMontant(d.prix_unitaire)}</TableCell>
                        <TableCell className="text-center">
                          {d.coup_de_pouce ? (
                            <Badge className="bg-[var(--oh-info-bg)] text-[var(--oh-info-text)]">
                              +{formatMontant(d.bonus_coup_pouce)}
                            </Badge>
                          ) : (
                            <span className="text-[var(--oh-text-secondary)]">-</span>
                          )}
                        </TableCell>
                        <TableCell className="text-center">
                          <Badge className={d.actif ? "bg-[var(--oh-success-bg)] text-[var(--oh-success-text)]" : "bg-[var(--oh-bg-elevated)]/20 text-[var(--oh-text-secondary)]"}>
                            {d.actif ? "Actif" : "Inactif"}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                // Router selon le type : un deal B2B s'édite dans la MODALE B2B
                                // dédiée (taux/fiche/phrase), pas la modale B2C générique.
                                if ((d.type || d.regime) === "b2b") {
                                  setDealB2BForm({
                                    id: d.id, nom: d.nom || "", delegataire_id: d.delegataire_id || "",
                                    delegataire_nom: d.delegataire_nom || "", fiche_cee: d.fiche_cee || "BAT-TH-125",
                                    prix_unitaire: d.prix_unitaire ?? 0, date_debut: d.date_debut || "",
                                    date_fin: d.date_fin || "", type: "b2b", actif: d.actif !== false,
                                    phrase: d.phrase || "",
                                  });
                                  setShowDealB2BDialog(true);
                                } else {
                                  setDealForm(d);
                                  setShowDealDialog(true);
                                }
                              }}
                              title="Modifier"
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => deleteDeal(d)}
                              title="Supprimer le deal"
                              aria-label="Supprimer le deal"
                            >
                              <Trash2 className="w-4 h-4 text-[var(--oh-error-text)]" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="text-center py-8">
                  <FileText className="w-12 h-12 text-[var(--oh-text-secondary)] mx-auto mb-4" />
                  <p className="text-[var(--oh-text-secondary)]">Aucun deal CEE configuré</p>
                  <p className="text-sm text-[var(--oh-text-secondary)]">Créez des deals avec vos délégataires</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Section Fiches CEE */}
          <Card className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]">
            <CardHeader className="flex flex-col gap-2 md:flex-row md:gap-0 items-center justify-between">
              <CardTitle className="text-[var(--oh-text)] flex items-center gap-2">
                <Leaf className="w-5 h-5 text-[var(--oh-success-text)]" />
                Fiches Opération CEE (ADEME)
              </CardTitle>
              <div className="flex items-center gap-3">
                {/* Recherche */}
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--oh-text-secondary)]" />
                  <Input
                    placeholder="Rechercher par code ou nom..."
                    value={searchFiche}
                    onChange={(e) => setSearchFiche(e.target.value)}
                    className="bg-[var(--oh-bg-surface)] border-[var(--oh-border)] pl-10 w-64"
                  />
                </div>
                <Button 
                  onClick={() => {
                    setFicheForm({ code: "", nom: "", categorie: "Chauffage", kwh_cumac_base: 0, kwh_cumac_precaire: 0, kwh_cumac_grand_precaire: 0, conditions: "", description_travaux: "", url_ademe: "", actif: true });
                    setShowFicheDialog(true);
                  }}
                  className="bg-[var(--oh-accent-solid)] hover:bg-[var(--oh-accent-solid)]"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Nouvelle fiche
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow className="border-[var(--oh-border)]">
                    <TableHead className="text-[var(--oh-text-secondary)]">Code</TableHead>
                    <TableHead className="text-[var(--oh-text-secondary)]">Nom</TableHead>
                    <TableHead className="text-[var(--oh-text-secondary)]">Catégorie</TableHead>
                    <TableHead className="text-[var(--oh-text-secondary)] text-right">kWh cumac (base)</TableHead>
                    <TableHead className="text-[var(--oh-text-secondary)] text-center">Statut</TableHead>
                    <TableHead className="text-[var(--oh-text-secondary)] text-center">PDF ADEME</TableHead>
                    <TableHead className="text-[var(--oh-text-secondary)] text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredFiches.map((f) => (
                    <TableRow key={f.code} className={`border-[var(--oh-border)] ${!f.actif ? 'opacity-50' : ''}`}>
                      <TableCell className="text-[var(--oh-success-text)] font-mono font-medium">{f.code}</TableCell>
                      <TableCell className="text-[var(--oh-text)]">{f.nom}</TableCell>
                      <TableCell>
                        <Badge className="bg-[var(--oh-bg-elevated)]">{f.categorie}</Badge>
                      </TableCell>
                      <TableCell className="text-right text-[var(--oh-text-secondary)] font-mono">
                        {(f.kwh_cumac_base || f.kwh_cumac || 0).toLocaleString("fr-FR")}
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge className={f.actif !== false ? "bg-[var(--oh-success-bg)] text-[var(--oh-success-text)]" : "bg-[var(--oh-bg-elevated)]/20 text-[var(--oh-text-secondary)]"}>
                          {f.actif !== false ? "Actif" : "Inactif"}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-center">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => window.open(f.url_ademe || getAdemeUrl(f.code), '_blank')}
                          title="Télécharger la fiche PDF ADEME"
                          className="text-[var(--oh-accent-text)] hover:text-[var(--oh-accent-text)]"
                        >
                          <Download className="w-4 h-4" />
                        </Button>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setSelectedFiche(f);
                              setShowFicheDetailDialog(true);
                            }}
                            title="Voir le détail"
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setFicheForm({
                                ...f,
                                kwh_cumac_base: f.kwh_cumac_base || f.kwh_cumac || 0
                              });
                              setShowFicheDialog(true);
                            }}
                            title="Modifier"
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => toggleFicheStatus(f)}
                            title={f.actif !== false ? "Désactiver" : "Activer"}
                          >
                            {f.actif !== false ? <Trash2 className="w-4 h-4 text-[var(--oh-error-text)]" /> : <CheckCircle className="w-4 h-4 text-[var(--oh-success-text)]" />}
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ============================================== */}
        {/* ONGLET MPR */}
        {/* ============================================== */}
        <TabsContent value="mpr" className="mt-6 space-y-6">
          {/* Header avec bouton Modifier */}
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-medium text-[var(--oh-text)]">Barèmes MaPrimeRénov' 2026</h3>
              <p className="text-[var(--oh-text-muted)] text-sm">Plafonds de revenus fiscaux par nombre de personnes</p>
            </div>
            <div className="flex gap-2">
              {editingBaremes ? (
                <>
                  <Button 
                    variant="outline" 
                    onClick={() => { setEditingBaremes(false); loadBaremesMPR(); }}
                    className="border-[var(--oh-border)]"
                  >
                    <X className="w-4 h-4 mr-2" />
                    Annuler
                  </Button>
                  <Button
                    onClick={saveBaremesMPR}
                    disabled={savingBaremes}
                    className="bg-[var(--oh-accent-solid)] hover:bg-[var(--oh-accent-solid)]"
                  >
                    <Save className="w-4 h-4 mr-2" />
                    {savingBaremes ? "Enregistrement..." : "Enregistrer"}
                  </Button>
                </>
              ) : (
                <Button 
                  onClick={() => setEditingBaremes(true)}
                  className="bg-[var(--oh-accent-solid)] hover:bg-[var(--oh-accent-solid)]"
                >
                  <Edit className="w-4 h-4 mr-2" />
                  Modifier
                </Button>
              )}
            </div>
          </div>

          {/* Barèmes de revenus */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Île-de-France */}
            <Card className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]">
              <CardHeader>
                <CardTitle className="text-[var(--oh-text)] flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-[var(--oh-accent-text)]" />
                  Barèmes Île-de-France
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow className="border-[var(--oh-border)]">
                      <TableHead className="text-[var(--oh-text-secondary)]">Personnes</TableHead>
                      <TableHead style={{ color: MPR_COL_TOKEN.bleu }}>Bleu</TableHead>
                      <TableHead style={{ color: MPR_COL_TOKEN.jaune }}>Jaune</TableHead>
                      <TableHead style={{ color: MPR_COL_TOKEN.violet }}>Violet</TableHead>
                      <TableHead style={{ color: MPR_COL_TOKEN.rose }}>Rose</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {[1, 2, 3, 4, 5].map((n) => (
                      <TableRow key={n} className="border-[var(--oh-border)]">
                        <TableCell className="text-[var(--oh-text)] font-medium">{n}</TableCell>
                        {["bleu", "jaune", "violet"].map((couleur) => (
                          <TableCell key={couleur} style={{ color: MPR_COL_TOKEN[couleur] }}>
                            {editingBaremes ? (
                              <Input
                                type="number"
                                value={baremesMPR.idf[n]?.[couleur] || ""}
                                onChange={(e) => updateBareme("idf", n, couleur, e.target.value)}
                                className="w-24 bg-[var(--oh-bg-surface)] border-[var(--oh-border)] text-[var(--oh-text)] text-center"
                              />
                            ) : (
                              `≤ ${(baremesMPR.idf[n]?.[couleur] || 0).toLocaleString("fr-FR")} €`
                            )}
                          </TableCell>
                        ))}
                        <TableCell style={{ color: MPR_COL_TOKEN.rose }}>
                          &gt; {(baremesMPR.idf[n]?.violet || 0).toLocaleString("fr-FR")} €
                        </TableCell>
                      </TableRow>
                    ))}
                    <TableRow className="border-[var(--oh-border)] bg-[var(--oh-bg-surface)]">
                      <TableCell className="text-[var(--oh-text-secondary)] text-sm">Par pers. sup.</TableCell>
                      {["bleu", "jaune", "violet"].map((couleur) => (
                        <TableCell key={couleur} className="text-sm" style={{ color: MPR_COL_TOKEN[couleur], opacity: 0.7 }}>
                          {editingBaremes ? (
                            <Input
                              type="number"
                              value={baremesMPR.idf.parPersonne?.[couleur] || ""}
                              onChange={(e) => updateBareme("idf", "parPersonne", couleur, e.target.value)}
                              className="w-20 bg-[var(--oh-bg-surface)] border-[var(--oh-border)] text-[var(--oh-text)] text-center text-sm"
                            />
                          ) : (
                            `+${(baremesMPR.idf.parPersonne?.[couleur] || 0).toLocaleString("fr-FR")} €`
                          )}
                        </TableCell>
                      ))}
                      <TableCell className="text-[var(--oh-text-secondary)] text-sm">-</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </CardContent>
            </Card>

            {/* Province */}
            <Card className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]">
              <CardHeader>
                <CardTitle className="text-[var(--oh-text)] flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-[var(--oh-success-text)]" />
                  Barèmes Province
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow className="border-[var(--oh-border)]">
                      <TableHead className="text-[var(--oh-text-secondary)]">Personnes</TableHead>
                      <TableHead style={{ color: MPR_COL_TOKEN.bleu }}>Bleu</TableHead>
                      <TableHead style={{ color: MPR_COL_TOKEN.jaune }}>Jaune</TableHead>
                      <TableHead style={{ color: MPR_COL_TOKEN.violet }}>Violet</TableHead>
                      <TableHead style={{ color: MPR_COL_TOKEN.rose }}>Rose</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {[1, 2, 3, 4, 5].map((n) => (
                      <TableRow key={n} className="border-[var(--oh-border)]">
                        <TableCell className="text-[var(--oh-text)] font-medium">{n}</TableCell>
                        {["bleu", "jaune", "violet"].map((couleur) => (
                          <TableCell key={couleur} style={{ color: MPR_COL_TOKEN[couleur] }}>
                            {editingBaremes ? (
                              <Input
                                type="number"
                                value={baremesMPR.province[n]?.[couleur] || ""}
                                onChange={(e) => updateBareme("province", n, couleur, e.target.value)}
                                className="w-24 bg-[var(--oh-bg-surface)] border-[var(--oh-border)] text-[var(--oh-text)] text-center"
                              />
                            ) : (
                              `≤ ${(baremesMPR.province[n]?.[couleur] || 0).toLocaleString("fr-FR")} €`
                            )}
                          </TableCell>
                        ))}
                        <TableCell style={{ color: MPR_COL_TOKEN.rose }}>
                          &gt; {(baremesMPR.province[n]?.violet || 0).toLocaleString("fr-FR")} €
                        </TableCell>
                      </TableRow>
                    ))}
                    <TableRow className="border-[var(--oh-border)] bg-[var(--oh-bg-surface)]">
                      <TableCell className="text-[var(--oh-text-secondary)] text-sm">Par pers. sup.</TableCell>
                      {["bleu", "jaune", "violet"].map((couleur) => (
                        <TableCell key={couleur} className="text-sm" style={{ color: MPR_COL_TOKEN[couleur], opacity: 0.7 }}>
                          {editingBaremes ? (
                            <Input
                              type="number"
                              value={baremesMPR.province.parPersonne?.[couleur] || ""}
                              onChange={(e) => updateBareme("province", "parPersonne", couleur, e.target.value)}
                              className="w-20 bg-[var(--oh-bg-surface)] border-[var(--oh-border)] text-[var(--oh-text)] text-center text-sm"
                            />
                          ) : (
                            `+${(baremesMPR.province.parPersonne?.[couleur] || 0).toLocaleString("fr-FR")} €`
                          )}
                        </TableCell>
                      ))}
                      <TableCell className="text-[var(--oh-text-secondary)] text-sm">-</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </div>

          {/* Légende des couleurs */}
          <Card className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]">
            <CardHeader>
              <CardTitle className="text-[var(--oh-text)]">Catégories de revenus MaPrimeRénov'</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {Object.entries(COULEURS_MPR).map(([key, val]) => (
                  <OhCard key={key} style={{ background: "var(--oh-bg-surface)" }}>
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-6 h-6 rounded-full shrink-0" style={{ background: val.token }} />
                      <span className="font-medium" style={{ color: val.token }}>{val.label}</span>
                    </div>
                    <p className="text-xs text-[var(--oh-text-secondary)]">
                      {key === "bleu" && "Revenus très modestes - Taux max 90%"}
                      {key === "jaune" && "Revenus modestes - Taux max 75%"}
                      {key === "violet" && "Revenus intermédiaires - Taux max 60%"}
                      {key === "rose" && "Revenus supérieurs - Taux max 40%"}
                    </p>
                  </OhCard>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Aides actuelles MPR - ADMINISTRATION DES FORFAITS */}
          <Card className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]">
            <CardHeader className="flex flex-col gap-2 md:flex-row md:gap-0 items-center justify-between">
              <div>
                <CardTitle className="text-[var(--oh-text)] flex items-center gap-2">
                  <Euro className="w-5 h-5 text-[var(--oh-accent-text)]" />
                  Forfaits MaPrimeRénov' - Vos équipements
                </CardTitle>
                <p className="text-xs text-[var(--oh-text-muted)] mt-1">
                  Personnalisez les montants selon vos installations
                </p>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setOperationMPRForm({
                      code: "",
                      nom: "",
                      categorie: "Chauffage",
                      forfait_bleu: 0,
                      forfait_jaune: 0,
                      forfait_violet: 0,
                      forfait_rose: 0,
                      unite: "€",
                      actif: true
                    });
                    setShowOperationMPRDialog(true);
                  }}
                  className="border-[var(--oh-success-border)] text-[var(--oh-success-text)]"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Ajouter équipement
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={loadOperationsMPR}
                  disabled={forfaitsMPRLoading}
                  className="border-[var(--oh-accent)] text-[var(--oh-accent-text)]"
                >
                  {forfaitsMPRLoading ? (
                    <Calculator className="w-4 h-4 animate-spin" />
                  ) : (
                    <RefreshCw className="w-4 h-4" />
                  )}
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {operationsMPR.length === 0 ? (
                <div className="text-center py-8">
                  <Calculator className="w-8 h-8 text-[var(--oh-text-secondary)] mx-auto mb-2" />
                  <p className="text-[var(--oh-text-secondary)] text-sm">Aucun équipement configuré</p>
                  <p className="text-[var(--oh-text-secondary)] text-xs mt-1">Cliquez sur "Ajouter équipement" pour commencer</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="border-[var(--oh-border)]">
                      <TableHead className="text-[var(--oh-text-secondary)]">Équipement</TableHead>
                      <TableHead className="text-right" style={{ color: MPR_COL_TOKEN.bleu }}>Bleu</TableHead>
                      <TableHead className="text-right" style={{ color: MPR_COL_TOKEN.jaune }}>Jaune</TableHead>
                      <TableHead className="text-right" style={{ color: MPR_COL_TOKEN.violet }}>Violet</TableHead>
                      <TableHead className="text-right" style={{ color: MPR_COL_TOKEN.rose }}>Rose</TableHead>
                      <TableHead className="text-[var(--oh-text-secondary)] text-right w-24">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {operationsMPR.map((op) => (
                      <TableRow key={op.id || op.code} className="border-[var(--oh-border)] hover:bg-[var(--oh-bg-elevated)]">
                        <TableCell className="text-[var(--oh-text)]">
                          <div>
                            <span className="font-medium">{op.nom || op.code}</span>
                            {op.code && <span className="text-[var(--oh-text-secondary)] text-xs ml-2">({op.code})</span>}
                            {op.unite && op.unite !== "€" && (
                              <Badge className="ml-2 text-xs bg-[var(--oh-bg-surface)] text-[var(--oh-text-secondary)]">{op.unite}</Badge>
                            )}
                          </div>
                          {op.categorie && (
                            <span className="text-xs text-[var(--oh-text-secondary)]">{op.categorie}</span>
                          )}
                        </TableCell>
                        <TableCell className="text-right font-mono" style={{ color: MPR_COL_TOKEN.bleu }}>
                          {op.forfait_bleu > 0 ? `${op.forfait_bleu.toLocaleString("fr-FR")} €` : "-"}
                        </TableCell>
                        <TableCell className="text-right font-mono" style={{ color: MPR_COL_TOKEN.jaune }}>
                          {op.forfait_jaune > 0 ? `${op.forfait_jaune.toLocaleString("fr-FR")} €` : "-"}
                        </TableCell>
                        <TableCell className="text-right font-mono" style={{ color: MPR_COL_TOKEN.violet }}>
                          {op.forfait_violet > 0 ? `${op.forfait_violet.toLocaleString("fr-FR")} €` : "-"}
                        </TableCell>
                        <TableCell className="text-right font-mono" style={{ color: MPR_COL_TOKEN.rose }}>
                          {op.forfait_rose > 0 ? `${op.forfait_rose.toLocaleString("fr-FR")} €` : "-"}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => {
                                setOperationMPRForm({
                                  id: op.id,
                                  code: op.code || "",
                                  nom: op.nom || "",
                                  categorie: op.categorie || "Chauffage",
                                  forfait_bleu: op.forfait_bleu || 0,
                                  forfait_jaune: op.forfait_jaune || 0,
                                  forfait_violet: op.forfait_violet || 0,
                                  forfait_rose: op.forfait_rose || 0,
                                  unite: op.unite || "€",
                                  actif: op.actif !== false
                                });
                                setShowOperationMPRDialog(true);
                              }}
                              className="text-[var(--oh-accent-text)] hover:text-[var(--oh-accent-text)] hover:bg-[var(--oh-accent-bg)] h-8 w-8 p-0"
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => deleteOperationMPR(op.id)}
                              className="text-[var(--oh-error-text)] hover:text-[var(--oh-error-text)] hover:bg-[var(--oh-error-bg)] h-8 w-8 p-0"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
              <div className="flex items-center justify-between mt-4 pt-4 border-t border-[var(--oh-border)]">
                <p className="text-xs text-[var(--oh-text-secondary)] italic flex items-center gap-1">
                  <Info className="w-3 h-3" />
                  {operationsMPR.length} équipement(s) configuré(s)
                  {operationsMPR[0]?.version && <span className="text-[var(--oh-accent-text)] ml-1">• Source: Barème ANAH {operationsMPR[0].version}</span>}
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={initDefaultOperationsMPR}
                  className="border-[var(--oh-border)] text-[var(--oh-text-muted)] text-xs"
                >
                  Réinitialiser (barème officiel)
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ============================================== */}
        {/* ONGLET PLAFONDS CUMUL */}
        {/* ============================================== */}
        <TabsContent value="plafonds" className="mt-6 space-y-6">
          <PlafondsCumulManager tenantId={tenantId} />
        </TabsContent>

        {/* ============================================== */}
        {/* ONGLET DOCUMENTS TEMPLATES */}
        {/* ============================================== */}
        <TabsContent value="documents" className="mt-6 space-y-6">
          <DocumentsEngineTemplates tenantId={tenantId} />
        </TabsContent>

        {/* ============================================== */}
        {/* ONGLET MODÈLES OFFICIELS (SOURCES) */}
        {/* ============================================== */}
        <TabsContent value="sources" className="mt-6 space-y-6">
          <DocumentsSourcesManager tenantId={tenantId} />
        </TabsContent>

        {/* ============================================== */}
        {/* ONGLET MENTIONS OBLIGATOIRES */}
        {/* ============================================== */}
        <TabsContent value="mentions" className="mt-6 space-y-6">
          <MentionsObligatoiresAides tenantId={tenantId} />
        </TabsContent>

        {/* ============================================== */}
        {/* ONGLET VARIABLES */}
        {/* ============================================== */}
        <TabsContent value="variables" className="mt-6 space-y-6">
          {isOmv && (
            <OmvConfigListEditor
              tenantId={tenantId}
              listType="statuts_omv"
              title="Statuts dossier OMV"
              description="Liste des statuts proposés sur les dossiers OMV (champ libre). Éditable : ajout, retrait, réordonnancement. Affichée dans la grille et filtrable."
              placeholder="Nouveau statut (ex : Devis envoyé)"
              savedMsg="Liste des statuts OMV enregistrée"
              dupMsg="Ce statut existe déjà"
            />
          )}
          {isOmv && (
            <OmvConfigListEditor
              tenantId={tenantId}
              listType="statuts_devis"
              title="Statuts devis OMV"
              description="Libellés et ordre des statuts du DEVIS (workflow Brouillon→Validé→Envoyé→Signé→En cours→À facturer→Facturé→Payé). Couche d'affichage : le statut technique reste géré par le système. Affiché sur le devis (fiche/grille/aperçu) + KPIs par statut."
              placeholder="Nouveau statut (ex : Facturé)"
              savedMsg="Liste des statuts devis enregistrée"
              dupMsg="Ce statut existe déjà"
            />
          )}
          {isOmv && (
            <OmvConfigListEditor
              tenantId={tenantId}
              listType="types_document"
              title="Types de documents"
              description="Liste des types proposés dans la déroulante « Type de document » de l'onglet Documents d'un dossier (ex : Formulaire CEE, Devis, Facture, Attestation…). Éditable : ajout, retrait, réordonnancement."
              placeholder="Nouveau type (ex : Formulaire CEE)"
              savedMsg="Liste des types de documents enregistrée"
              dupMsg="Ce type existe déjà"
              slugifyId={true}
            />
          )}
          {isOmv && (
            <OmvConfigListEditor
              tenantId={tenantId}
              listType="secteurs_activite"
              title="Secteurs d'activité"
              description="Liste des secteurs d'activité proposés pour l'entreprise bénéficiaire (création de dossier OMV). Éditable : ajout, retrait, réordonnancement."
              placeholder="Nouveau secteur (ex : Santé)"
              savedMsg="Liste des secteurs d'activité enregistrée"
              dupMsg="Ce secteur existe déjà"
            />
          )}
          <VariablesManager />
        </TabsContent>

        {/* ============================================== */}
        {/* ONGLET MAPRIME ADAPT' */}
        {/* ============================================== */}
        {featureFlags.mpa_enabled && (
          <TabsContent value="mpa" className="mt-6 space-y-6">
            <SimulateurMPA tenantId={tenantId} />
          </TabsContent>
        )}
      </Tabs>

      {/* ============================================== */}
      {/* DIALOG DÉLÉGATAIRE */}
      {/* ============================================== */}
      <Dialog open={showDelegataireDialog} onOpenChange={(open) => {
        setShowDelegataireDialog(open);
        if (!open) {
          setSearchEntreprise("");
          setSearchResults([]);
          setShowSearchResults(false);
        }
      }}>
        <DialogContent className="bg-[var(--oh-bg-surface)] border-[var(--oh-border)] max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-[var(--oh-text)] flex items-center gap-2">
              <Building2 className="w-5 h-5 text-[var(--oh-accent-text)]" />
              {delegataireForm.id ? "Modifier le délégataire" : "Nouveau délégataire"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-6 py-4">
            
            {/* Section Recherche d'entreprise */}
            <div className="space-y-2">
              <label className="text-sm text-[var(--oh-success-text)] font-medium flex items-center gap-2">
                <Search className="w-4 h-4" />
                Rechercher une entreprise (nom ou SIREN)
              </label>
              <div className="relative">
                <Input
                  value={searchEntreprise}
                  onChange={(e) => setSearchEntreprise(e.target.value)}
                  className="bg-[var(--oh-bg-elevated)] border-[var(--oh-success-border)] focus:border-[var(--oh-success-border)] pl-10"
                  placeholder="Tapez un nom d'entreprise ou un SIREN..."
                />
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--oh-text-secondary)]" />
                {searchLoading && (
                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    <div className="w-4 h-4 border-2 border-[var(--oh-success-border)] border-t-transparent rounded-full animate-spin" />
                  </div>
                )}
              </div>
              <p className="text-xs text-[var(--oh-text-secondary)] flex items-center gap-1">
                <Info className="w-3 h-3" />
                Recherche via recherche-entreprises.api.gouv.fr
              </p>
              
              {/* Résultats de recherche */}
              {showSearchResults && searchResults.length > 0 && (
                <div className="bg-[var(--oh-bg-elevated)] border border-[var(--oh-border)] rounded-lg max-h-48 overflow-y-auto">
                  {searchResults.map((e, idx) => (
                    <div
                      key={idx}
                      onClick={() => selectEntreprise(e)}
                      className="p-3 hover:bg-[var(--oh-bg-elevated)] cursor-pointer border-b border-[var(--oh-border)] last:border-b-0"
                    >
                      <p className="text-[var(--oh-text)] font-medium">{e.nom_complet}</p>
                      <p className="text-xs text-[var(--oh-text-secondary)]">
                        SIREN: {e.siren} | {e.siege?.libelle_commune}
                      </p>
                    </div>
                  ))}
                </div>
              )}
              {showSearchResults && searchResults.length === 0 && searchEntreprise.length >= 3 && !searchLoading && (
                <p className="text-sm text-[var(--oh-text-secondary)] italic">Aucune entreprise trouvée</p>
              )}
            </div>

            <hr className="border-[var(--oh-border)]" />

            {/* Informations légales (auto-remplies) */}
            <div className="space-y-4">
              <h4 className="text-sm font-medium text-[var(--oh-text-secondary)]">Identité légale</h4>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-xs text-[var(--oh-text-secondary)]">Raison sociale *</label>
                  <Input
                    value={delegataireForm.nom}
                    onChange={(e) => setDelegataireForm({...delegataireForm, nom: e.target.value})}
                    className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]"
                    placeholder="Nom de l'entreprise"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-xs text-[var(--oh-text-secondary)]">Forme juridique</label>
                  <Input
                    value={delegataireForm.forme_juridique}
                    onChange={(e) => setDelegataireForm({...delegataireForm, forme_juridique: e.target.value})}
                    className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]"
                    placeholder="SARL, SAS..."
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-xs text-[var(--oh-text-secondary)]">SIREN</label>
                  <Input
                    value={delegataireForm.siren}
                    onChange={(e) => setDelegataireForm({...delegataireForm, siren: e.target.value})}
                    className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)] font-mono"
                    placeholder="123 456 789"
                    maxLength={9}
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-xs text-[var(--oh-text-secondary)]">SIRET *</label>
                  <Input
                    value={delegataireForm.siret}
                    onChange={(e) => setDelegataireForm({...delegataireForm, siret: e.target.value})}
                    className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)] font-mono"
                    placeholder="123 456 789 00012"
                    maxLength={14}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-xs text-[var(--oh-text-secondary)]">Code NAF</label>
                  <Input
                    value={delegataireForm.code_naf}
                    onChange={(e) => setDelegataireForm({...delegataireForm, code_naf: e.target.value})}
                    className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)] font-mono"
                    placeholder="4322A"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-xs text-[var(--oh-text-secondary)]">N° TVA Intracommunautaire</label>
                  <Input
                    value={delegataireForm.tva_intracommunautaire}
                    onChange={(e) => setDelegataireForm({...delegataireForm, tva_intracommunautaire: e.target.value})}
                    className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)] font-mono"
                    placeholder="FR12345678901"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-xs text-[var(--oh-text-secondary)]">Capital social</label>
                  <div className="relative">
                    <Input
                      type="number"
                      value={delegataireForm.capital_social}
                      onChange={(e) => setDelegataireForm({...delegataireForm, capital_social: parseFloat(e.target.value) || 0})}
                      className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)] pr-8"
                      placeholder="10000"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--oh-text-secondary)]">€</span>
                  </div>
                </div>
                <div className="space-y-2">
                  <label className="text-xs text-[var(--oh-text-secondary)]">Adresse</label>
                  <Input
                    value={delegataireForm.adresse}
                    onChange={(e) => setDelegataireForm({...delegataireForm, adresse: e.target.value})}
                    className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]"
                    placeholder="Adresse complète"
                  />
                </div>
              </div>
            </div>

            <hr className="border-[var(--oh-border)]" />

            {/* Contact */}
            <div className="space-y-4">
              <h4 className="text-sm font-medium text-[var(--oh-text-secondary)]">Contact</h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <label className="text-xs text-[var(--oh-text-secondary)]">Nom du contact</label>
                  <Input
                    value={delegataireForm.contact_nom}
                    onChange={(e) => setDelegataireForm({...delegataireForm, contact_nom: e.target.value})}
                    className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]"
                    placeholder="Jean Dupont"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-xs text-[var(--oh-text-secondary)]">Email</label>
                  <Input
                    type="email"
                    value={delegataireForm.contact_email}
                    onChange={(e) => setDelegataireForm({...delegataireForm, contact_email: e.target.value})}
                    className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]"
                    placeholder="contact@entreprise.fr"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-xs text-[var(--oh-text-secondary)]">Téléphone</label>
                  <Input
                    value={delegataireForm.contact_telephone}
                    onChange={(e) => setDelegataireForm({...delegataireForm, contact_telephone: e.target.value})}
                    className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]"
                    placeholder="06 00 00 00 00"
                  />
                </div>
              </div>
            </div>

            <hr className="border-[var(--oh-border)]" />

            {/* Paramètres CEE */}
            <div className="space-y-4">
              <h4 className="text-sm font-medium text-[var(--oh-text-secondary)]">Paramètres CEE</h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-xs text-[var(--oh-text-secondary)]">Prix kWh cumac (€)</label>
                  <Input
                    type="number"
                    step="0.0001"
                    value={delegataireForm.prix_kwh_cumac}
                    onChange={(e) => setDelegataireForm({...delegataireForm, prix_kwh_cumac: parseFloat(e.target.value)})}
                    className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]"
                    placeholder="0.0065"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-xs text-[var(--oh-text-secondary)]">Statut</label>
                  <Select
                    value={delegataireForm.actif ? "actif" : "inactif"}
                    onValueChange={(v) => setDelegataireForm({...delegataireForm, actif: v === "actif"})}
                  >
                    <SelectTrigger className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="actif">Actif</SelectItem>
                      <SelectItem value="inactif">Inactif</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDelegataireDialog(false)}>
              Annuler
            </Button>
            <Button onClick={saveDelegataire} className="bg-[var(--oh-accent-solid)] hover:bg-[var(--oh-accent-solid)]">
              {delegataireForm.id ? "Modifier" : "Créer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ============================================== */}
      {/* DIALOG DEAL CEE */}
      {/* ============================================== */}
      {/* Deal B2B (profil installateur) — variante DISTINCTE, sans précarité/coup de pouce. */}
      <Dialog open={showDealB2BDialog} onOpenChange={setShowDealB2BDialog}>
        <DialogContent className="bg-[var(--oh-bg-surface)] border-[var(--oh-border)] max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-[var(--oh-text)]">{dealB2BForm.id ? "Modifier le deal B2B" : "Nouveau deal B2B"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-[var(--oh-text-secondary)]">Nom du deal</Label>
              <Input value={dealB2BForm.nom} onChange={e => setDealB2BForm({ ...dealB2BForm, nom: e.target.value })}
                placeholder="ex : Deal ACE BAT-TH-125 2026" className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)] text-[var(--oh-text)]" />
            </div>
            <div>
              <Label className="text-[var(--oh-text-secondary)]">Délégataire</Label>
              <select value={dealB2BForm.delegataire_id} onChange={e => setDealB2BForm({ ...dealB2BForm, delegataire_id: e.target.value })}
                className="flex h-10 w-full rounded-md border border-[var(--oh-border)] bg-[var(--oh-bg-elevated)] px-3 py-2 text-sm text-[var(--oh-text)]">
                <option value="">— Sélectionner —</option>
                {(delegataires || []).map(d => (<option key={d.id} value={d.id}>{d.nom}{d.siren ? ` (${d.siren})` : ""}</option>))}
              </select>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-[var(--oh-text-secondary)]">Fiche CEE</Label>
                <select value={dealB2BForm.fiche_cee} onChange={e => setDealB2BForm({ ...dealB2BForm, fiche_cee: e.target.value })}
                  className="flex h-10 w-full rounded-md border border-[var(--oh-border)] bg-[var(--oh-bg-elevated)] px-3 py-2 text-sm text-[var(--oh-text)]" data-testid="deal-b2b-fiche">
                  {["BAT-TH-125", "BAT-TH-126", "BAR-TH-125", "BAR-TH-127"].map(f => (<option key={f} value={f}>{f}</option>))}
                </select>
              </div>
              <div>
                <Label className="text-[var(--oh-text-secondary)]">Prix unitaire (€ / cumac)</Label>
                <Input type="number" step="0.0001" value={dealB2BForm.prix_unitaire}
                  onChange={e => setDealB2BForm({ ...dealB2BForm, prix_unitaire: e.target.value })}
                  placeholder="ex : 0.0025" className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)] text-[var(--oh-text)]" data-testid="deal-b2b-prix" />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-[var(--oh-text-secondary)]">Date début</Label>
                <Input type="date" value={dealB2BForm.date_debut} onChange={e => setDealB2BForm({ ...dealB2BForm, date_debut: e.target.value })}
                  className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)] text-[var(--oh-text)]" />
              </div>
              <div>
                <Label className="text-[var(--oh-text-secondary)]">Date fin</Label>
                <Input type="date" value={dealB2BForm.date_fin} onChange={e => setDealB2BForm({ ...dealB2BForm, date_fin: e.target.value })}
                  className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)] text-[var(--oh-text)]" />
              </div>
            </div>
            <div>
              <Label className="text-[var(--oh-text-secondary)]">Phrase prime (devis) — variables {"{montant}"} {"{delegataire}"} {"{siren}"}</Label>
              <textarea value={dealB2BForm.phrase} onChange={e => setDealB2BForm({ ...dealB2BForm, phrase: e.target.value })}
                rows={3} className="flex w-full rounded-md border border-[var(--oh-border)] bg-[var(--oh-bg-elevated)] px-3 py-2 text-sm text-[var(--oh-text)]"
                data-testid="deal-b2b-phrase" />
              <p className="text-xs text-[var(--oh-text-muted)] mt-1">Apparaît sous la prime sur le devis. Les variables sont remplacées dynamiquement.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDealB2BDialog(false)} className="border-[var(--oh-border)]">Annuler</Button>
            <Button onClick={saveDealB2B} disabled={loading || !dealB2BForm.nom} className="bg-[var(--oh-accent-solid)] hover:bg-[var(--oh-accent-solid)]" data-testid="deal-b2b-save">Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showDealDialog} onOpenChange={setShowDealDialog}>
        <DialogContent className="bg-[var(--oh-bg-surface)] border-[var(--oh-border)] max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-[var(--oh-text)]">
              {dealForm.id ? "Modifier le deal" : "Nouveau deal CEE"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label className="text-sm text-[var(--oh-text-secondary)]">Nom du deal *</label>
              <Input
                value={dealForm.nom}
                onChange={(e) => setDealForm({...dealForm, nom: e.target.value})}
                className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]"
                placeholder="Ex: Deal PAC 2025"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm text-[var(--oh-text-secondary)]">Délégataire *</label>
              <Select
                value={dealForm.delegataire_id}
                onValueChange={(v) => setDealForm({...dealForm, delegataire_id: v})}
              >
                <SelectTrigger className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]">
                  <SelectValue placeholder="Sélectionner" />
                </SelectTrigger>
                <SelectContent>
                  {delegataires.map(d => (
                    <SelectItem key={d.id} value={d.id}>{d.nom}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <label className="text-sm text-[var(--oh-text-secondary)]">Fiche CEE *</label>
              <Select
                value={dealForm.fiche_cee}
                onValueChange={(v) => setDealForm({...dealForm, fiche_cee: v})}
              >
                <SelectTrigger className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]">
                  <SelectValue placeholder="Sélectionner" />
                </SelectTrigger>
                <SelectContent>
                  {(fichesCEE.length > 0 ? fichesCEE : FICHES_CEE_ADEME).filter(f => f.actif !== false).map(f => (
                    <SelectItem key={f.code} value={f.code}>{f.code} - {f.nom}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <label className="text-sm text-[var(--oh-text-secondary)]">Type de précarité *</label>
              <Select
                value={dealForm.type_precarite || "classic"}
                onValueChange={(v) => setDealForm({...dealForm, type_precarite: v})}
              >
                <SelectTrigger className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="grand_precaire">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-[var(--oh-accent-solid)]" />
                      Grand précaire
                    </div>
                  </SelectItem>
                  <SelectItem value="precaire">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full" style={{ background: "var(--oh-stat-orange-text)" }} />
                      Précaire
                    </div>
                  </SelectItem>
                  <SelectItem value="classic">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-[var(--oh-bg-elevated)]" />
                      Classic
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm text-[var(--oh-text-secondary)]">Prix unitaire (€)</label>
                <Input
                  type="number"
                  value={dealForm.prix_unitaire}
                  onChange={(e) => setDealForm({...dealForm, prix_unitaire: parseFloat(e.target.value)})}
                  className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]"
                  placeholder="500"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm text-[var(--oh-text-secondary)]">Coup de Pouce</label>
                <Select
                  value={dealForm.coup_de_pouce ? "oui" : "non"}
                  onValueChange={(v) => setDealForm({...dealForm, coup_de_pouce: v === "oui"})}
                >
                  <SelectTrigger className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="non">Non</SelectItem>
                    <SelectItem value="oui">Oui</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            {dealForm.coup_de_pouce && (
              <div className="space-y-2">
                <label className="text-sm text-[var(--oh-text-secondary)]">Bonus Coup de Pouce (€)</label>
                <Input
                  type="number"
                  value={dealForm.bonus_coup_pouce}
                  onChange={(e) => setDealForm({...dealForm, bonus_coup_pouce: parseFloat(e.target.value)})}
                  className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]"
                  placeholder="1000"
                />
              </div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm text-[var(--oh-text-secondary)]">Date début</label>
                <Input
                  type="date"
                  value={dealForm.date_debut}
                  onChange={(e) => setDealForm({...dealForm, date_debut: e.target.value})}
                  className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm text-[var(--oh-text-secondary)]">Date fin</label>
                <Input
                  type="date"
                  value={dealForm.date_fin}
                  onChange={(e) => setDealForm({...dealForm, date_fin: e.target.value})}
                  className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDealDialog(false)}>
              Annuler
            </Button>
            <Button onClick={saveDeal} className="bg-[var(--oh-accent-solid)] hover:bg-[var(--oh-accent-solid)]">
              {dealForm.id ? "Modifier" : "Créer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ============================================== */}
      {/* DIALOG FICHE CEE */}
      {/* ============================================== */}
      <Dialog open={showFicheDialog} onOpenChange={setShowFicheDialog}>
        <DialogContent className="bg-[var(--oh-bg-surface)] border-[var(--oh-border)] max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-[var(--oh-text)]">
              {ficheForm.id ? "Modifier la fiche CEE" : "Nouvelle fiche CEE"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm text-[var(--oh-text-secondary)]">Code *</label>
                <Input
                  value={ficheForm.code}
                  onChange={(e) => setFicheForm({...ficheForm, code: e.target.value.toUpperCase()})}
                  className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)] font-mono"
                  placeholder="BAR-TH-XXX"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm text-[var(--oh-text-secondary)]">Catégorie</label>
                <Select
                  value={ficheForm.categorie}
                  onValueChange={(v) => setFicheForm({...ficheForm, categorie: v})}
                >
                  <SelectTrigger className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Chauffage">Chauffage</SelectItem>
                    <SelectItem value="ECS">ECS</SelectItem>
                    <SelectItem value="Isolation">Isolation</SelectItem>
                    <SelectItem value="Ventilation">Ventilation</SelectItem>
                    <SelectItem value="Autre">Autre</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <label className="text-sm text-[var(--oh-text-secondary)]">Nom *</label>
              <Input
                value={ficheForm.nom}
                onChange={(e) => setFicheForm({...ficheForm, nom: e.target.value})}
                className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]"
                placeholder="Nom de l'opération"
              />
            </div>
            {/* 🔴 LE DESCRIPTIF QUI PART SUR LES DOCUMENTS COMMERCIAUX.
                ⛔ Laissé vide, la section « Descriptif des travaux » ⛔ ne
                s'affiche PAS sur le devis : OmniHub ⛔ n'invente aucun texte
                contractuel. */}
            <div className="space-y-2">
              <label className="text-sm text-[var(--oh-text-secondary)]" htmlFor="fiche-descriptif">
                Descriptif des travaux — imprimé sur le devis et la facture
              </label>
              <Textarea
                id="fiche-descriptif"
                data-testid="fiche-description-travaux"
                rows={4}
                value={ficheForm.description_travaux || ""}
                onChange={(e) => setFicheForm({...ficheForm, description_travaux: e.target.value})}
                className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]"
                placeholder="Décrivez les travaux tels qu'ils doivent apparaître sur les documents commerciaux (dépose, fourniture, pose, raccordement, mise en service…). Laissé vide, aucune section ne s'affiche."
              />
              <p className="text-xs" style={{ color: "var(--oh-text-muted)" }}>
                Saisi une fois ici, il est repris par tous les nouveaux documents.
                Un document déjà émis garde le texte qu&apos;il portait.
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <label className="text-sm text-[var(--oh-text-secondary)]">kWh cumac (base)</label>
                <Input
                  type="number"
                  value={ficheForm.kwh_cumac_base}
                  onChange={(e) => setFicheForm({...ficheForm, kwh_cumac_base: parseInt(e.target.value)})}
                  className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]"
                  placeholder="0"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm text-[var(--oh-info-text)]">kWh (précaire)</label>
                <Input
                  type="number"
                  value={ficheForm.kwh_cumac_precaire}
                  onChange={(e) => setFicheForm({...ficheForm, kwh_cumac_precaire: parseInt(e.target.value)})}
                  className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]"
                  placeholder="0"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm text-[var(--oh-accent-text)]">kWh (grand préc.)</label>
                <Input
                  type="number"
                  value={ficheForm.kwh_cumac_grand_precaire}
                  onChange={(e) => setFicheForm({...ficheForm, kwh_cumac_grand_precaire: parseInt(e.target.value)})}
                  className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]"
                  placeholder="0"
                />
              </div>
            </div>
            <div className="space-y-2">
              <label className="text-sm text-[var(--oh-text-secondary)]">Conditions / Notes</label>
              <Input
                value={ficheForm.conditions}
                onChange={(e) => setFicheForm({...ficheForm, conditions: e.target.value})}
                className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]"
                placeholder="Conditions d'éligibilité, notes..."
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm text-[var(--oh-text-secondary)]">URL fiche ADEME (PDF)</label>
              <Input
                value={ficheForm.url_ademe}
                onChange={(e) => setFicheForm({...ficheForm, url_ademe: e.target.value})}
                className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]"
                placeholder="https://..."
              />
              <p className="text-xs text-[var(--oh-text-secondary)]">
                Laissez vide pour utiliser l'URL automatique basée sur le code
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowFicheDialog(false)}>
              Annuler
            </Button>
            <Button onClick={saveFicheCEE} className="bg-[var(--oh-accent-solid)] hover:bg-[var(--oh-accent-solid)]">
              {ficheForm.id ? "Modifier" : "Créer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ============================================== */}
      {/* DIALOG DÉTAIL FICHE CEE */}
      {/* ============================================== */}
      <Dialog open={showFicheDetailDialog} onOpenChange={setShowFicheDetailDialog}>
        <DialogContent className="bg-[var(--oh-bg-surface)] border-[var(--oh-border)] max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-[var(--oh-text)] flex items-center gap-2">
              <FileText className="w-5 h-5 text-[var(--oh-success-text)]" />
              {selectedFiche?.code} - {selectedFiche?.nom}
            </DialogTitle>
          </DialogHeader>
          {selectedFiche && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-[var(--oh-text-secondary)] mb-1">Code</p>
                  <p className="text-[var(--oh-success-text)] font-mono font-medium">{selectedFiche.code}</p>
                </div>
                <div>
                  <p className="text-xs text-[var(--oh-text-secondary)] mb-1">Catégorie</p>
                  <Badge className="bg-[var(--oh-bg-elevated)]">{selectedFiche.categorie}</Badge>
                </div>
              </div>
              <div>
                <p className="text-xs text-[var(--oh-text-secondary)] mb-1">Nom</p>
                <p className="text-[var(--oh-text)]">{selectedFiche.nom}</p>
              </div>
              <div className="bg-[var(--oh-bg-elevated)] p-4 rounded-lg space-y-2">
                <p className="text-sm text-[var(--oh-text-secondary)] font-medium">kWh cumac par type de ménage</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 text-center">
                  <div>
                    <p className="text-xs text-[var(--oh-text-secondary)]">Classic</p>
                    <p className="text-lg text-[var(--oh-text)] font-mono">
                      {(selectedFiche.kwh_cumac_base || selectedFiche.kwh_cumac || 0).toLocaleString("fr-FR")}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-[var(--oh-info-text)]">Précaire</p>
                    <p className="text-lg text-[var(--oh-info-text)] font-mono">
                      {(selectedFiche.kwh_cumac_precaire || 0).toLocaleString("fr-FR")}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-[var(--oh-accent-text)]">Grand précaire</p>
                    <p className="text-lg text-[var(--oh-accent-text)] font-mono">
                      {(selectedFiche.kwh_cumac_grand_precaire || 0).toLocaleString("fr-FR")}
                    </p>
                  </div>
                </div>
              </div>
              {selectedFiche.conditions && (
                <div>
                  <p className="text-xs text-[var(--oh-text-secondary)] mb-1">Conditions</p>
                  <p className="text-[var(--oh-text-secondary)] text-sm">{selectedFiche.conditions}</p>
                </div>
              )}
              <div className="pt-4 border-t border-[var(--oh-border)]">
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => window.open(selectedFiche.url_ademe || getAdemeUrl(selectedFiche.code), '_blank')}
                >
                  <Download className="w-4 h-4 mr-2" />
                  Télécharger la fiche PDF (ADEME)
                </Button>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowFicheDetailDialog(false)}>
              Fermer
            </Button>
            <Button 
              onClick={() => {
                setFicheForm({
                  ...selectedFiche,
                  kwh_cumac_base: selectedFiche.kwh_cumac_base || selectedFiche.kwh_cumac || 0
                });
                setShowFicheDetailDialog(false);
                setShowFicheDialog(true);
              }}
              className="bg-[var(--oh-accent-solid)] hover:bg-[var(--oh-accent-solid)]"
            >
              Modifier
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ============================================== */}
      {/* DIALOG OPÉRATION MPR */}
      {/* ============================================== */}
      <Dialog open={showOperationMPRDialog} onOpenChange={setShowOperationMPRDialog}>
        <DialogContent className="bg-[var(--oh-bg-surface)] border-[var(--oh-border)] max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-[var(--oh-text)]">
              {operationMPRForm.id ? "Modifier l'opération MPR" : "Nouvelle opération MPR"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm text-[var(--oh-text-secondary)]">Code *</label>
                <Input
                  value={operationMPRForm.code}
                  onChange={(e) => setOperationMPRForm({...operationMPRForm, code: e.target.value.toUpperCase()})}
                  className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)] font-mono"
                  placeholder="BAR-TH-XXX"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm text-[var(--oh-text-secondary)]">Catégorie</label>
                <Select
                  value={operationMPRForm.categorie}
                  onValueChange={(v) => setOperationMPRForm({...operationMPRForm, categorie: v})}
                >
                  <SelectTrigger className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Chauffage">Chauffage</SelectItem>
                    <SelectItem value="ECS">ECS</SelectItem>
                    <SelectItem value="Isolation">Isolation</SelectItem>
                    <SelectItem value="Ventilation">Ventilation</SelectItem>
                    <SelectItem value="Autre">Autre</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <label className="text-sm text-[var(--oh-text-secondary)]">Nom *</label>
              <Input
                value={operationMPRForm.nom}
                onChange={(e) => setOperationMPRForm({...operationMPRForm, nom: e.target.value})}
                className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]"
                placeholder="Nom de l'opération"
              />
            </div>
            <div className="space-y-2">
              <p className="text-sm text-[var(--oh-text-secondary)] font-medium">Plafonds MaPrimeRénov' (€)</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-2">
                <div className="space-y-1">
                  <label className="text-xs" style={{ color: MPR_COL_TOKEN.bleu }}>Bleu</label>
                  <Input
                    type="number"
                    value={operationMPRForm.plafond_bleu}
                    onChange={(e) => setOperationMPRForm({...operationMPRForm, plafond_bleu: parseInt(e.target.value)})}
                    className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-xs" style={{ color: MPR_COL_TOKEN.jaune }}>Jaune</label>
                  <Input
                    type="number"
                    value={operationMPRForm.plafond_jaune}
                    onChange={(e) => setOperationMPRForm({...operationMPRForm, plafond_jaune: parseInt(e.target.value)})}
                    className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-xs" style={{ color: MPR_COL_TOKEN.violet }}>Violet</label>
                  <Input
                    type="number"
                    value={operationMPRForm.plafond_violet}
                    onChange={(e) => setOperationMPRForm({...operationMPRForm, plafond_violet: parseInt(e.target.value)})}
                    className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-xs" style={{ color: MPR_COL_TOKEN.rose }}>Rose</label>
                  <Input
                    type="number"
                    value={operationMPRForm.plafond_rose}
                    onChange={(e) => setOperationMPRForm({...operationMPRForm, plafond_rose: parseInt(e.target.value)})}
                    className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]"
                  />
                </div>
              </div>
            </div>
            <div className="space-y-2">
              <label className="text-sm text-[var(--oh-text-secondary)]">Conditions</label>
              <Input
                value={operationMPRForm.conditions}
                onChange={(e) => setOperationMPRForm({...operationMPRForm, conditions: e.target.value})}
                className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]"
                placeholder="Conditions d'éligibilité..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowOperationMPRDialog(false)}>
              Annuler
            </Button>
            <Button onClick={saveOperationMPR} className="bg-[var(--oh-accent-solid)] hover:bg-[var(--oh-accent-solid)]">
              {operationMPRForm.id ? "Modifier" : "Créer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
